/*
 * PerfSim — canvas UI for /perfboard-simulator.
 *
 * Owns rendering and gestures ONLY. The board (geometry, electrical model, router)
 * lives in board.js; the solver is the circuit-simulator's CircuitEngine; pan/zoom,
 * undo, share, props and the pointer state-machine come from SimCore.
 *
 * This tool does NOT fork the simulator engine. The breadboard did, once, and every
 * bug its tester found was born in the divergence — so the seams here are callbacks
 * into shared code, never copies of it.
 *
 * ── The view flip ──
 * You look at a board from the component side, but you SOLDER it from the other side,
 * where everything is mirrored. Getting that backwards is the classic way to ruin a
 * build, so the flip is a first-class control, not a nicety: press it and the board
 * mirrors horizontally, hole labels follow, and the solder side comes forward.
 *
 * Pure ES5 — served raw, no build step. `function(){}` only; no arrows, no `??`/`?.`.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var T = window.PB_I18N || {};
  function t(k, d) { return (T && typeof T[k] === 'string') ? T[k] : (d != null ? d : k); }

  var CE = window.CircuitEngine;
  var SC = window.SimCore;
  var PB = window.PerfBoard;
  if (!SC || !SC.util || !SC.camera || !SC.pointer || !SC.history || !SC.share || !SC.props || !PB || !CE) {
    try { if (window.__ae) window.__ae('tool_error', { slug: 'perfboard-simulator', name: 'sim-core-missing' }); } catch (e) {}
    return;
  }
  var Board = PB.Board;
  var esc = SC.util.esc, b64e = SC.util.b64e, b64d = SC.util.b64d;
  var isLight = SC.util.isLight, rr = SC.util.roundRect, line = SC.util.line, shade = SC.util.shade;

  /* ---------------- component catalog ---------------- */
  // `span` = holes between the first and last leg at 2.54 mm pitch — the real
  // footprint of the through-hole part, which is why a 1/4 W resistor needs 4.
  var CAT = {
    battery:       { pins: 2, span: 2, vals: { voltage: 9, rint: 0.5 } },
    // Insulated wire jumper on the COMPONENT side — the standard way to cross traces
    // on a one-sided board (Ivan, #6822: "минус в темнице из плюсов"). Not an engine
    // component: the board merges its two pads at the connectivity level.
    jumper:        { pins: 2, span: 3, vals: {} },
    resistor:      { pins: 2, span: 4, vals: { resistance: 470 } },
    potentiometer: { pins: 2, span: 3, vals: { resistance: 10000, wiper: 0.5 } },
    led:           { pins: 2, span: 1, vals: { vf: 2.0, imax: 0.02, color: '#ef4444' } },
    diode:         { pins: 2, span: 3, vals: { vf: 0.7 } },
    capacitor:     { pins: 2, span: 2, vals: { capacitance: 100 } },
    button:        { pins: 2, span: 2, vals: { pressed: false } },
    switch:        { pins: 2, span: 2, vals: { closed: false } },
    photoresistor: { pins: 2, span: 2, vals: { rmin: 1000, rmax: 200000, level: 0.5 } },
    buzzer:        { pins: 2, span: 2, vals: { resistance: 100 } },
    transistor:    { pins: 3, span: 2, vals: { kind: 'npn', hfe: 100, icmax: 0.5, pmax: 0.625 } },
    // выпрямительный мост одной деталью (ромб из 4 диодов) — до этого фото-
    // распознаватель честно писал «bridge типа нет, разложил диодом» и терял
    // пол-схемы светильника (Иван 30.07, п.4). Движок мост умеет давно.
    bridge:        { pins: 4, span: 3, vals: { vf: 0.7 } },
    mcu:           { pins: 6, span: 2, layout: 'dip', rowGap: 3, vals: { program: 'blink', interval: 500, vdd: 5,
                     pinModes: ['in', 'in', 'in', 'out', 'out', 'out'],
                     outStates: [false, false, false, false, false, false] } },
    // parts the auto-router already lays out from an imported schematic — now placeable by
    // hand too, so the palette lists everything the tool actually supports (Ivan 2026-07-24:
    // "во вкладке доступных компонентов указаны не все").
    lamp:          { pins: 2, span: 2, vals: { resistance: 100, pmax: 1 } },
    inductor:      { pins: 2, span: 3, vals: { inductance: 0.001 } },
    fuse:          { pins: 2, span: 3, vals: { imax: 1 } },
    motor:         { pins: 2, span: 2, vals: { resistance: 50 } },
    thermistor:    { pins: 2, span: 2, vals: { rmin: 1000, rmax: 100000, level: 0.5 } },
    // ── «Монтаж»: детали БЕЗ электрической модели (Иван 30.07: «ассортимент и
    // реалистичность важнее моделирования — это разработка пайки»). Паяются,
    // занимают дырки, видны на стороне пайки и в шагах сборки; решатель их
    // ПРОПУСКАЕТ (Board.MOUNT_TYPES), связность пинов они НЕ объединяют (в
    // отличие от jumper — у панельки каждая нога сама по себе). Модель больше
    // не цена добавления детали: этот список можно наращивать пачками. ──
    socket8:       { pins: 8,  span: 3, layout: 'dip', rowGap: 3, mount: true, socket: true, vals: {} },
    socket14:      { pins: 14, span: 6, layout: 'dip', rowGap: 3, mount: true, socket: true, vals: {} },
    socket16:      { pins: 16, span: 7, layout: 'dip', rowGap: 3, mount: true, socket: true, vals: {} },
    // генерик-микросхемы «под ключ» с РЕДАКТИРУЕМОЙ подписью (Иван 30.07:
    // «микросхемы с распиновкой под ключ, без названий» — подписывает сам)
    chip8:         { pins: 8,  span: 3, layout: 'dip', rowGap: 3, mount: true, vals: { label: '' } },
    chip14:        { pins: 14, span: 6, layout: 'dip', rowGap: 3, mount: true, vals: { label: '' } },
    chip16:        { pins: 16, span: 7, layout: 'dip', rowGap: 3, mount: true, vals: { label: '' } },
    header4:       { pins: 4,  span: 3, mount: true, vals: {} },
    header8:       { pins: 8,  span: 7, mount: true, vals: {} },
    terminal2:     { pins: 2,  span: 2, mount: true, vals: {} },
    terminal3:     { pins: 3,  span: 4, mount: true, vals: {} },
    module4:       { pins: 4,  span: 3, mount: true, vals: {} }
  };
  var REFPFX = { battery: 'BAT', jumper: 'J', resistor: 'R', potentiometer: 'P', led: 'LED', diode: 'D',
    capacitor: 'C', button: 'BTN', switch: 'SW', photoresistor: 'LDR', buzzer: 'BZ',
    transistor: 'Q', mcu: 'IC', lamp: 'LA', inductor: 'L', fuse: 'F', motor: 'M', thermistor: 'TH',
    socket8: 'XS', socket14: 'XS', socket16: 'XS', header4: 'XP', header8: 'XP',
    terminal2: 'XT', terminal3: 'XT', module4: 'MOD',
    bridge: 'BR', chip8: 'DD', chip14: 'DD', chip16: 'DD' };

  var RES_COLORS = ['#000000', '#8B4513', '#ff0000', '#ff8c00', '#ffff00', '#00b050', '#0070c0', '#7030a0', '#808080', '#ffffff'];
  var RES_GOLD = '#d4af37', RES_SILVER = '#c0c0c0';
  // Real 4-band code: two significant digits + multiplier. Sub-100 Ω uses a gold
  // (x0.1) or silver (x0.01) multiplier, not a leading-zero band.
  function resistorBands(ohms) {
    if (!(ohms > 0)) return ['#000000', '#000000', RES_GOLD];
    var exp = 0, v = ohms;
    while (v >= 100) { v /= 10; exp++; }
    while (v < 10) { v *= 10; exp--; }
    var m = Math.round(v);
    if (m >= 100) { m = Math.round(m / 10); exp++; }
    var d1 = Math.floor(m / 10) % 10, d2 = m % 10;
    var mult = exp === -1 ? RES_GOLD : (exp === -2 ? RES_SILVER : RES_COLORS[Math.max(0, Math.min(9, exp))]);
    return [RES_COLORS[d1] || '#000', RES_COLORS[d2] || '#000', mult || '#000'];
  }

  // Bottom traces are tinned-solder GREY (Ivan asked for grey; a soldered joint really
  // is silver-grey, not copper), the top side a muted blue so the two are still told
  // apart on a two-sided board.
  var LAYER_COL = ['#9aa0aa', '#3f9ae0'];
  var PITCH = Board.PITCH;

  /* ---------------- app ---------------- */
  function PerfSim() {
    this.board = new Board({ cols: 24, rows: 18, kind: 'perf', sides: 1 });
    this.cam = { x: 0, y: 0, s: 1 };
    this.mode = 'select';          // select | trace | cut | via | place:<type>
    this.layer = 0;                // layer new traces land on
    this.flipped = false;          // viewing the solder side (mirrored)
    this.selected = null;          // component id
    this.selTrace = null;          // {layer,a,b}
    this.target = null;            // imported netlist to check against, or null
    this.sim = null; this.simulating = false;
    this.pointers = {}; this.drag = null; this.panning = null; this.pinch = null; this.wiring = null;
    this._undo = []; this._redo = [];
    this._traceFrom = -1; this._lastHole = -1; this.hotHole = -1;
    this._saveT = 0; this._simT = 0; this.raf = 0; this.phase = 0;
    this._ideal = false;
  }

  /* ---------------- lifecycle ---------------- */
  PerfSim.prototype.init = function () {
    var self = this, $ = function (id) { return document.getElementById(id); };
    this.$ = $;
    this.canvas = $('pbCanvas'); this.wrap = $('pbCanvasWrap'); this.actions = $('pbActions');
    if (!this.canvas || !this.wrap) return;
    this.ctx = this.canvas.getContext('2d');

    if (!this._loadFromUrl()) this._restore();
    try { if (window.__toolSettings) this._ideal = !!window.__toolSettings.load('perfboard-simulator', 'ideal', false); } catch (e) {}

    this._resize();
    if (window.ResizeObserver) {
      var rt = 0;
      this._ro = new ResizeObserver(function () { clearTimeout(rt); rt = setTimeout(function () { self._resize(); }, 60); });
      this._ro.observe(this.wrap);
    }
    window.addEventListener('resize', function () { self._resize(); });

    this._bindUI(); this._bindPointer();
    this._bindScrollbars({ h: 'pbHBar', v: 'pbVBar' });
    this._syncBoardControls();
    this._fitBoard();
    this._updateUndoUI();
    this.renderAssembly();
    this.draw();
  };

  PerfSim.prototype._resize = function () {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var r = this.wrap.getBoundingClientRect();
    this.cssW = Math.max(200, r.width); this.cssH = Math.max(200, r.height);
    this.canvas.width = Math.round(this.cssW * dpr); this.canvas.height = Math.round(this.cssH * dpr);
    this.canvas.style.width = this.cssW + 'px'; this.canvas.style.height = this.cssH + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._updateScrollbars();
    this.draw();
  };

  /** Fit the whole board in view — the default framing on first open. */
  PerfSim.prototype._fitBoard = function () {
    var bw = this.board.width(), bh = this.board.height();
    var s = SC.camera.clampScale(Math.min((this.cssW - 24) / bw, (this.cssH - 24) / bh));
    this.cam.s = s;
    this.cam.x = (this.cssW - bw * s) / 2;
    this.cam.y = (this.cssH - bh * s) / 2;
    this._updateScrollbars();
  };

  /* ---------------- view flip ----------------
   * Mirroring is a pure VIEW transform: the model never changes, so a flipped
   * board still simulates and shares identically. Screen→world has to undo it,
   * which is the one place a mirror is easy to get wrong.
   */
  PerfSim.prototype._mirrorX = function (wx) {
    return this.flipped ? (this.board.width() - wx) : wx;
  };
  PerfSim.prototype.worldPoint = function (cx, cy) {
    var w = this.screenToWorld(cx, cy);
    return { x: this._mirrorX(w.x), y: w.y };
  };
  PerfSim.prototype.setFlipped = function (v) {
    this.flipped = !!v;
    var b = this.$('pbFlipBtn');
    if (b) {
      b.classList.toggle('active', this.flipped);
      b.innerHTML = (this.flipped ? '🔄 ' : '🔄 ') + esc(this.flipped ? t('view_solder', 'Solder side') : t('view_component', 'Component side'));
    }
    try { if (window.__toolSettings) window.__toolSettings.save('perfboard-simulator', 'flipped', this.flipped ? 1 : 0); } catch (e) {}
    this.draw();
  };

  /* ---------------- persistence / share ---------------- */
  PerfSim.prototype._persist = function () {
    if (this._sharedView) return;
    var self = this; clearTimeout(this._saveT);
    this._saveT = setTimeout(function () {
      try {
        if (window.__toolSettings) window.__toolSettings.save('perfboard-simulator', 'board', JSON.stringify(self.serializeState()));
      } catch (e) {}
    }, 350);
  };
  PerfSim.prototype._restore = function () {
    try {
      if (!window.__toolSettings) return;
      var raw = window.__toolSettings.load('perfboard-simulator', 'board', null);
      if (raw) this.applyState(typeof raw === 'string' ? JSON.parse(raw) : raw);
      this.flipped = !!window.__toolSettings.load('perfboard-simulator', 'flipped', 0);
    } catch (e) {}
  };

  PerfSim.prototype.serializeState = function () {
    return { b: this.board.serialize(), target: this.target };
  };
  PerfSim.prototype.applyState = function (d) {
    if (!d) return;
    var b = Board.deserialize(d.b || d);
    if (b) this.board = b;
    this.target = d.target || null;
    this.selected = null; this.selTrace = null;
  };

  PerfSim.prototype._shareBtnId = 'pbShareBtn';
  PerfSim.prototype._t = function (k, d) { return t(k, d); };
  PerfSim.prototype._encodeShare = function () { return b64e(JSON.stringify(this.serializeState())); };
  PerfSim.prototype._decodeShare = function (code) {
    var s = b64d(code); if (!s) return false;
    var d; try { d = JSON.parse(s); } catch (e) { return false; }
    if (!d || !d.b) return false;
    this.applyState(d);
    return true;
  };
  PerfSim.prototype._isShareEmpty = function () {
    return !this.board.components.length && !this.board.traces.length;
  };
  PerfSim.prototype._afterShareLoad = function () { this._fitBoard(); this._syncBoardControls(); };

  /* ---------------- history seams ---------------- */
  PerfSim.prototype._undoBtnId = 'pbUndoBtn';
  PerfSim.prototype._redoBtnId = 'pbRedoBtn';
  PerfSim.prototype._postHistory = function () { this._showProps(null); this._after(); };

  // NB: _after must NOT touch the props panel. Closing it here ate the panel on every
  // keystroke in the breadboard; paths that DO need it closed call _showProps(null).
  PerfSim.prototype._after = function (f) {
    if (this.board.traces.length) this._stage('wired');
    this.renderAssembly();
    this._hideActions();
    if (this.simulating) this._scheduleSim();
    this._updateUndoUI();
    this._updateStatus();
    this._persist();
    this.draw();
    if (f) this._ae('feature_use', { f: f });
  };
  PerfSim.prototype._ae = function (ev, p) {
    try { if (window.__ae) { p = p || {}; p.slug = 'perfboard-simulator'; window.__ae(ev, p); } } catch (e) {}
  };
  /**
   * Funnel marker. Fires ONCE per stage per session, so the numbers read as "how many
   * people got this far" rather than "how many times somebody clicked" — the second
   * kind of number is what makes a funnel useless.
   *
   * board → parts → wired → verified → exported
   */
  PerfSim.prototype._stage = function (name, extra) {
    this._stages = this._stages || {};
    if (this._stages[name]) return;
    this._stages[name] = true;
    var p = extra || {};
    p.from = this._lastStage || null;
    p.to = name;
    this._lastStage = name;
    this._ae('tool_stage', p);
  };
  /** Something the user would call a failure. Kept separate from feature_use. */
  PerfSim.prototype._aeError = function (name, extra) {
    var p = extra || {};
    p.name = name;
    this._ae('tool_error', p);
  };
  PerfSim.prototype._nextRef = function (type) {
    var pfx = REFPFX[type] || 'U', max = 0;
    this.board.components.forEach(function (c) {
      if (c.ref && c.ref.indexOf(pfx) === 0) {
        var n = parseInt(c.ref.slice(pfx.length), 10);
        if (isFinite(n) && n > max) max = n;
      }
    });
    return pfx + (max + 1);
  };

  /* ---------------- board configuration ---------------- */
  PerfSim.prototype._syncBoardControls = function () {
    var k = this.$('pbKind'), s = this.$('pbSides'), c = this.$('pbCols'), r = this.$('pbRows');
    if (k) k.value = this.board.kind;
    if (s) s.value = this.board.plated ? '2p' : String(this.board.sides);
    if (c) c.value = String(this.board.cols);
    if (r) r.value = String(this.board.rows);
    var vb = this.$('pbViaBtn');
    if (vb) vb.disabled = this.board.sides !== 2 || this.board.plated;   // plating = every hole is already through
    var lb = this.$('pbLayerSel');
    if (lb) { lb.disabled = this.board.sides !== 2; lb.value = String(this.layer); }
    var cb = this.$('pbCutBtn');
    if (cb) cb.disabled = this.board.kind !== 'strip';
    this.setFlipped(this.flipped);
  };

  /**
   * Rebuild the board at a new size/kind. Traces and parts that no longer fit are
   * dropped — say so out loud rather than silently losing the user's work.
   */
  PerfSim.prototype.reconfigure = function (patch) {
    this._beginChange();
    var old = this.board;
    var b = new Board({
      cols: patch.cols != null ? patch.cols : old.cols,
      rows: patch.rows != null ? patch.rows : old.rows,
      kind: patch.kind != null ? patch.kind : old.kind,
      sides: patch.sides != null ? patch.sides : old.sides,
      plated: patch.plated != null ? patch.plated : old.plated
    });
    // remap by (col,row) so parts keep their physical position, not their index
    var dropped = 0, i;
    function remap(h) {
      var c = old.col(h), r = old.row(h);
      return b.inBounds(c, r) ? b.idx(c, r) : -1;
    }
    for (i = 0; i < old.components.length; i++) {
      var comp = old.components[i], holes = [], okAll = true;
      for (var p = 0; p < comp.holes.length; p++) {
        var nh = remap(comp.holes[p]);
        if (nh < 0) { okAll = false; break; }
        holes.push(nh);
      }
      if (okAll && b.canPlace(holes)) b.components.push({ id: comp.id, type: comp.type, ref: comp.ref, holes: holes, props: comp.props });
      else dropped++;
    }
    for (i = 0; i < old.traces.length; i++) {
      var tr = old.traces[i], a = remap(tr.a), bb = remap(tr.b);
      if (a >= 0 && bb >= 0 && tr.layer <= b.maxLayer() && b.adjacent(a, bb)) b.addTrace(tr.layer, a, bb);
      else if (a >= 0 || bb >= 0) dropped++;
    }
    for (i = 0; i < old.vias.length; i++) {
      var v = remap(old.vias[i]);
      if (v >= 0 && b.sides === 2 && !b.plated) b.vias.push(v);   // plating makes them moot
    }
    if (b.kind === 'strip') {
      for (i = 0; i < old.cuts.length; i++) {
        var ca = remap(old.cuts[i].a), cb2 = remap(old.cuts[i].b);
        if (ca >= 0 && cb2 >= 0) b.cuts.push({ a: Math.min(ca, cb2), b: Math.max(ca, cb2) });
      }
    }
    b.nextId = old.nextId;
    this.board = b;
    if (this.layer > b.maxLayer()) this.layer = 0;
    this.selected = null; this.selTrace = null;
    this._syncBoardControls();
    this._fitBoard();
    this._stage('board', { kind: b.kind, sides: b.sides });
    if (dropped) this._toast(t('resize_dropped', 'Some parts fell outside the new board and were removed.').replace('{n}', dropped), 'warning');
    this._showProps(null);
    this._after('reconfigure');
  };

  PerfSim.prototype._toast = function (msg, type) {
    try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (e) {}
  };

  /* ---------------- modes ---------------- */
  PerfSim.prototype.setMode = function (m) {
    this.mode = m;
    this._traceFrom = -1;
    var map = { select: 'pbSelectBtn', trace: 'pbTraceBtn', cut: 'pbCutBtn', via: 'pbViaBtn', note: 'pbNoteBtn' };
    for (var k in map) { var el = this.$(map[k]); if (el) el.classList.toggle('active', m === k); }
    var pal = document.querySelectorAll('#pbPalette .pb-comp-btn');
    for (var i = 0; i < pal.length; i++) {
      pal[i].classList.toggle('active', m === 'place:' + pal[i].getAttribute('data-type'));
    }
    // teach each gesture ONCE per page view — picking four parts in a row used to
    // stack four identical hint toasts (caught on my own wave-19 QA screenshot)
    this._hintShown = this._hintShown || {};
    if (m.indexOf('place:') === 0 && !this._hintShown.place) { this._hintShown.place = 1; this._toast(t('place_hint', 'Tap a hole to drop the part, or drag between two holes.'), 'info'); }
    if (m === 'note' && !this._hintShown.note) { this._hintShown.note = 1; this._toast(t('note_hint', 'Tap a hole to label it — for a part the palette does not have.'), 'info'); }
    this.draw();
  };

  /* ---------------- pointer ---------------- */
  PerfSim.prototype._onPointerDown = function (w0, e) {
    var w = { x: this._mirrorX(w0.x), y: w0.y };
    var b = this.board, h = b.holeAt(w.x, w.y);

    if (this.mode === 'trace' && h >= 0) { this._traceFrom = h; this._lastHole = h; return; }
    if (this.mode === 'cut' && h >= 0) { this._traceFrom = h; this._lastHole = h; return; }
    if (this.mode === 'via' && h >= 0) { this._traceFrom = h; return; }
    if (this.mode.indexOf('place:') === 0 && h >= 0) { this._traceFrom = h; return; }

    // select mode: grab a component to drag it — by a leg hole OR anywhere on its body
    if (this.mode === 'select') {
      var comp = (h >= 0 && b.componentAtHole(h)) || b.componentAtXY(w.x, w.y);
      if (comp) {
        var onLeg = h >= 0 && comp.holes.indexOf(h) >= 0;
        var grab = onLeg ? h : b.nearestHoleOf(comp, w.x, w.y);
        this.drag = { id: comp.id, from: comp.holes.slice(), grabHole: grab, moved: false };
        // A JUMPER grabbed by a LEG stretches: the leg follows the finger to any
        // free hole, like a real wire cut to length (Ivan 03.08: «почему она
        // всегда на несколько точек? С ней ничего нельзя делать»). Grabbing the
        // arc still moves the whole part; palette span 3 is just the start.
        if (onLeg && comp.type === 'jumper') this.drag.leg = comp.holes.indexOf(h);
        return;
      }
    }
    // nothing under the finger → arm panning
    this.panning = { x: e.clientX, y: e.clientY, camx: this.cam.x, camy: this.cam.y };
  };

  PerfSim.prototype._onPointerMove = function (w0, e) {
    var w = { x: this._mirrorX(w0.x), y: w0.y };
    var b = this.board, h = b.holeAt(w.x, w.y);
    this.hotHole = h;

    if (this.panning && this._downMoved) {
      this.cam.x = this.panning.camx + (e.clientX - this.panning.x);
      this.cam.y = this.panning.camy + (e.clientY - this.panning.y);
      this._updateScrollbars();
      this.draw();
      return;
    }
    // dragging a trace: lay one unit edge per hole crossed, so a swipe draws a run
    if ((this.mode === 'trace' || this.mode === 'cut') && this._traceFrom >= 0 && h >= 0 && h !== this._lastHole) {
      if (b.adjacent(this._lastHole, h)) {
        if (!this._pendingChange) { this._beginChange(); this._pendingChange = true; }
        if (this.mode === 'trace') b.addTrace(this.layer, this._lastHole, h);
        else b.toggleCut(this._lastHole, h);
        this._lastHole = h;
        this.draw();
        return;
      }
      this._lastHole = h;
    }
    if (this.drag && this._downMoved && h >= 0) {
      var comp = this._compById(this.drag.id);
      if (comp) {
        if (this.drag.leg != null) {
          // stretch: ONE leg follows the finger, the other stays put
          var other = comp.holes[1 - this.drag.leg];
          if (h !== other) {
            var stretched = comp.holes.slice();
            stretched[this.drag.leg] = h;
            if (b.canPlace(stretched, comp.id, comp.type)) { comp.holes = stretched; this.drag.moved = true; }
          }
        } else {
          var moved = this._shiftHoles(comp.holes, this.drag.from, this.drag.grabHole, h);
          if (moved && b.canPlace(moved, comp.id, comp.type)) { comp.holes = moved; this.drag.moved = true; }
        }
      }
    }
    this.draw();
  };

  /** Translate a part's legs by the grab delta, keeping it on the board. */
  PerfSim.prototype._shiftHoles = function (cur, from, grabHole, toHole) {
    var b = this.board;
    var dc = b.col(toHole) - b.col(grabHole), dr = b.row(toHole) - b.row(grabHole);
    var out = [];
    for (var i = 0; i < from.length; i++) {
      var c = b.col(from[i]) + dc, r = b.row(from[i]) + dr;
      if (!b.inBounds(c, r)) return null;
      out.push(b.idx(c, r));
    }
    return out;
  };

  PerfSim.prototype._onPointerUp = function (w0) {
    var w = { x: this._mirrorX(w0.x), y: w0.y };
    var b = this.board, h = b.holeAt(w.x, w.y);
    var wasDrag = this.drag, movedTrace = this._pendingChange;
    this._pendingChange = false;

    if (movedTrace) { this._traceFrom = -1; this._lastHole = -1; this._after(this.mode); return; }

    if (!this._downMoved) {
      // a tap
      if (this.mode.indexOf('place:') === 0 && h >= 0) { this._placeAt(this.mode.slice(6), h); this._traceFrom = -1; return; }
      if (this.mode === 'via' && h >= 0) { this._beginChange(); b.toggleVia(h); this._after('via'); this._traceFrom = -1; return; }
      if (this.mode === 'note' && h >= 0) { this._editNote(h); this._traceFrom = -1; return; }
      if (this.mode === 'trace' && h >= 0) { this._traceFrom = -1; this._selectTraceAt(w); return; }
      if (this.mode === 'select') {
        var comp = (h >= 0 && b.componentAtHole(h)) || b.componentAtXY(w.x, w.y);
        if (comp) { this.selected = comp.id; this.selTrace = null; this._showProps(comp); this._positionActions(); this.draw(); return; }
        if (!this._selectTraceAt(w)) { this.selected = null; this.selTrace = null; this._showProps(null); this._hideActions(); this.draw(); }
        return;
      }
    } else if (wasDrag) {
      if (wasDrag.moved) {
        // committed a move — re-route if we know what the board is supposed to be
        this._beginChange();
        this._sharedView = false;
        if (this.target) this._rerouteQuiet();
        else b.retraceMoved(wasDrag.id, wasDrag.from);   // hand-wired board: copper follows the part (Ivan 30.07)
        this._after('move');
        this._positionActions();
        // the open props panel still shows the PRE-drag holes — refresh it
        if (this.selected === wasDrag.id) { var mc = this._compById(wasDrag.id); if (mc) this._showProps(mc); }
        return;
      }
      // grabbed a part but a shaky finger never moved it off its hole — that is a tap, so
      // SELECT it instead of doing nothing (Ivan: parts "react only after several touches").
      var gc = this._compById(wasDrag.id);
      if (gc) { this.selected = gc.id; this.selTrace = null; this._showProps(gc); this._positionActions(); this.draw(); return; }
    }
    this._traceFrom = -1; this._lastHole = -1;
    this.draw();
  };

  PerfSim.prototype._onPointerCancel = function () { this._traceFrom = -1; this._lastHole = -1; this._pendingChange = false; };

  /**
   * Double-tap a switch or a button to flip it — the same gesture the circuit
   * simulator documents. Ivan built battery → switch → lamp, tapped the switch,
   * nothing happened, and reported "лампа не работает" (#6432): the only way to
   * close the switch here was a toggle row buried in the properties panel.
   * (Skeleton-detected double-tap, NOT the dblclick event — see sim-core/pointer.js.)
   */
  PerfSim.prototype._onDoubleTap = function (w0) {
    var w = { x: this._mirrorX(w0.x), y: w0.y };
    var b = this.board, h = b.holeAt(w.x, w.y);
    var comp = (h >= 0 && b.componentAtHole(h)) || b.componentAtXY(w.x, w.y);
    if (!comp) return;
    var prop = comp.type === 'switch' ? 'closed' : (comp.type === 'button' ? 'pressed' : null);
    if (!prop) return;
    this._beginChange();
    comp.props[prop] = !comp.props[prop];
    if (this.selected === comp.id) this._showProps(comp);
    this._after('toggle_' + prop);
  };

  PerfSim.prototype._compById = function (id) {
    for (var i = 0; i < this.board.components.length; i++) if (this.board.components[i].id === id) return this.board.components[i];
    return null;
  };

  /** Hit-test the trace nearest a world point (traces sit between holes, not on them). */
  PerfSim.prototype._selectTraceAt = function (w) {
    var b = this.board, best = null, bd = PITCH * 0.4;
    for (var i = 0; i < b.traces.length; i++) {
      var tr = b.traces[i];
      var pa = b.holeXY(tr.a), pb = b.holeXY(tr.b);
      var mx = (pa.x + pb.x) / 2, my = (pa.y + pb.y) / 2;
      var d = Math.hypot(w.x - mx, w.y - my);
      if (d < bd) { bd = d; best = tr; }
    }
    if (best) {
      this.selTrace = best; this.selected = null;
      this._showTraceProps(best); this._positionActions(); this.draw();
      return true;
    }
    return false;
  };

  /* ---------------- placing parts ---------------- */
  PerfSim.prototype._placeAt = function (type, hole) {
    var b = this.board, spec = CAT[type];
    if (!spec) return;
    // Same footprint logic the auto-placer uses, so the smart block's two-row DIP lands
    // its six legs in six distinct holes instead of failing "those holes are taken".
    var holes = b.legHoles(spec, b.col(hole), b.row(hole));
    if (!holes) { this._toast(t('no_fit', 'The part does not fit here — try further from the edge.'), 'warning'); return; }
    if (!b.canPlace(holes, null, type)) { this._toast(t('occupied', 'Those holes are already taken.'), 'warning'); return; }
    this._beginChange();
    var comp = {
      id: 'p' + (b.nextId++), type: type, ref: this._nextRef(type), holes: holes,
      props: JSON.parse(JSON.stringify(spec.vals))
    };
    b.components.push(comp);
    this.selected = comp.id; this.selTrace = null;
    this._showProps(comp);
    this._positionActions();
    this._stage('parts');
    // Placement mode is a one-shot: drop the part and return to Select, instead of
    // staying armed and stamping another part on the next tap. Ivan hit this twice
    // ("stays in a pressed mode, appears again and again") — a sticky place-mode is a
    // classic confusion, and one-tap-one-part is what every editor he expects does.
    this.setMode('select');
    this._after('place_' + type);
  };

  PerfSim.prototype.deleteSelected = function () {
    var b = this.board;
    if (this.selected) {
      this._beginChange();
      b.components = b.components.filter(function (c) { return c.id !== this.selected; }, this);
      this.selected = null;
    } else if (this.selTrace) {
      this._beginChange();
      b.removeTrace(this.selTrace.layer, this.selTrace.a, this.selTrace.b);
      this.selTrace = null;
    } else return;
    this._showProps(null);
    this._after('delete');
  };

  PerfSim.prototype.rotateSelected = function () {
    var comp = this._compById(this.selected);
    if (!comp) return;
    var b = this.board, h0 = comp.holes[0];
    var c0 = b.col(h0), r0 = b.row(h0), out = [h0], i;
    // rotate the leg axis 90°: what ran along the row now runs down the column
    for (i = 1; i < comp.holes.length; i++) {
      var dc = b.col(comp.holes[i]) - c0, dr = b.row(comp.holes[i]) - r0;
      var nc = c0 - dr, nr = r0 + dc;
      if (!b.inBounds(nc, nr)) { this._toast(t('rotate_no_room', 'Not enough room to rotate here.'), 'warning'); return; }
      out.push(b.idx(nc, nr));
    }
    if (!b.canPlace(out, comp.id, comp.type)) { this._toast(t('rotate_no_room', 'Not enough room to rotate here.'), 'warning'); return; }
    this._beginChange();
    var beforeRotate = comp.holes;
    comp.holes = out;
    if (this.target) this._rerouteQuiet();
    else b.retraceMoved(comp.id, beforeRotate);          // copper follows a rotation too
    this._positionActions();
    this._after('rotate');
  };

  PerfSim.prototype.clearAll = function () {
    this._beginChange();
    this.board.components = []; this.board.traces = []; this.board.vias = []; this.board.cuts = [];
    // notes too — «метка не удаляется при полной очистке схемы» (Ivan 30.07):
    // clear-all left the labels floating over an empty board
    this.board.notes = {};
    this.board.nextId = 1;
    this.selected = null; this.selTrace = null; this.sim = null;
    this._showProps(null);
    this._after('clear');
  };

  /* ---------------- importing a schematic ---------------- */
  /**
   * Paste a circuit-simulator link/code or a typed connection list, and lay it out.
   *
   * Placement happens on the CURRENT board, so if it does not fit we say so and change
   * nothing — silently resizing the user's board out from under them would be worse
   * than a refusal they can act on.
   */
  PerfSim.prototype.loadSchematic = function (text) {
    var imp = PB.importer.auto(text);
    if (!imp.ok) {
      this._aeError('import_failed', { unsupported: imp.unsupported.slice(0, 4).join(',') });
      if (imp.unsupported.length) this._toast(t('import_unsupported', 'Not recognised: {n}').replace('{n}', imp.unsupported.slice(0, 4).join(', ')), 'error');
      else this._toast(t('import_failed', 'Could not read that. Paste a link from the circuit simulator, or a list like "R1.1 - LED1.2".'), 'error');
      this._ae('feature_use', { f: 'import_fail' });
      return false;
    }

    this._beginChange();
    var snapshot = JSON.parse(JSON.stringify(this.board.serialize()));
    var laid = this.board.autolayout(imp.specs, imp.target);
    if (!laid.placed) {
      var back = Board.deserialize(snapshot);
      if (back) this.board = back;
      this._aeError('import_no_room', { parts: imp.specs.length, cols: this.board.cols, rows: this.board.rows });
      this._toast(t('import_no_room', 'The parts do not fit on this board — add rows or columns and try again.'), 'warning');
      this._after();
      return false;
    }
    this.target = imp.target;
    var routed = laid.route;
    this.selected = null; this.selTrace = null;
    this._showProps(null);
    this._fitBoard();
    this._after('import');

    if (imp.unsupported.length) {
      this._toast(t('import_unsupported', 'Not recognised: {n}').replace('{n}', imp.unsupported.slice(0, 4).join(', ')), 'warning');
    }
    if (routed.ok) this._toast(t('import_ok', 'Laid out {n} parts. Check the board before you solder.').replace('{n}', imp.specs.length), 'success');
    else if (laid.jumpers && this.board.verify(this.target).ok) {
      // non-planar circuit: copper alone cannot route it on one side, so the layout
      // was completed with insulated wire jumpers — a finished board, not a failure
      this._toast(t('import_ok_jumpers', 'Laid out {n} parts — {m} crossings are done with wire jumpers, like on a real board.')
        .replace('{n}', imp.specs.length).replace('{m}', laid.jumpers), 'success');
    }
    else this._toast(t('route_partial', 'Routed {n} of {m} nets.')
      .replace('{n}', routed.routed).replace('{m}', routed.routed + routed.failed.length), 'warning');
    this._ae('tool_complete', { f: 'import', parts: imp.specs.length, routed: routed.routed, failed: routed.failed.length, jumpers: laid.jumpers || 0 });
    return true;
  };

  /* ---------------- photo → netlist (the one paid lane) ---------------- */

  /**
   * Downsample to the model's actual working resolution BEFORE upload.
   * A modern phone shoots ~4000 px wide; everything past 2576 is image tokens the
   * user pays for and the model never uses. Also re-encodes to JPEG, which turns a
   * 9 MB HEIC-ish PNG into a few hundred KB with no loss that matters for reading
   * a schematic.
   */
  PerfSim.prototype._prepPhoto = function (file, cb) {
    var maxEdge = (window.PB_VISION && window.PB_VISION.maxEdge) || 2576;
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      var w = img.naturalWidth, h = img.naturalHeight;
      var scale = Math.min(1, maxEdge / Math.max(w, h));
      var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
      var cv = document.createElement('canvas');
      cv.width = cw; cv.height = ch;
      var cx = cv.getContext('2d');
      cx.fillStyle = '#fff'; cx.fillRect(0, 0, cw, ch);   // flatten alpha; schematics are on paper
      cx.drawImage(img, 0, 0, cw, ch);
      URL.revokeObjectURL(url);
      var data = cv.toDataURL('image/jpeg', 0.9);
      cb(null, data.replace(/^data:[^;]+;base64,/, ''), 'image/jpeg');
    };
    img.onerror = function () { URL.revokeObjectURL(url); cb(new Error('decode')); };
    img.src = url;
  };

  PerfSim.prototype._photoStatus = function (msg, kind) {
    var el = this.$('pbPhotoStatus');
    if (!el) return;
    if (!msg) { el.style.display = 'none'; return; }
    el.style.display = ''; el.className = 'pb-photo-status ' + (kind || 'busy');
    el.textContent = msg;
  };

  PerfSim.prototype.readPhoto = function () {
    var self = this, cfg = window.PB_VISION;
    if (!cfg || !cfg.enabled || !this._photoFile) return;
    if (this._photoBusy) return;
    this._photoBusy = true;
    var btn = this.$('pbPhotoBtn');
    if (btn) btn.disabled = true;
    this._photoStatus(t('photo_reading', 'Reading the schematic…'), 'busy');
    this._ae('feature_use', { f: 'photo_read' });

    this._prepPhoto(this._photoFile, function (err, b64, mime) {
      if (err) { self._photoDone(t('photo_failed', 'Could not read the photo.'), 'err'); return; }
      var token = '';
      try { var m = document.querySelector('meta[name=csrf-token]'); token = m ? m.getAttribute('content') : ''; } catch (e) {}
      fetch(cfg.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token, 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ image: b64, mime: mime })
      }).then(function (r) {
        return r.json().then(function (j) { return { status: r.status, body: j }; });
      }).then(function (res) {
        if (res.status === 401) { self._photoDone(t('photo_need_login', 'Sign in to use this.'), 'err'); return; }
        if (res.status === 402) { self._photoDone(t('photo_need_tokens', 'Not enough tokens.'), 'err'); return; }
        if (res.status === 429) { self._photoDone(t('photo_rate_limited', 'Too many attempts — wait a moment.'), 'warn'); return; }
        if (res.status === 413 || (res.body && res.body.error === 'too_big_pixels')) { self._photoDone(t('photo_too_big', 'That image is too large.'), 'err'); return; }
        if (!res.body || !res.body.ok) {
          var e = res.body ? res.body.error : '';
          var msg = e === 'vision_refused' ? t('photo_refused', 'The service declined to read that image.')
            : (e === 'vision_unreadable' || e === 'vision_no_connections')
              ? t('photo_unreadable', 'No circuit could be read from that photo — try a sharper, straighter shot.')
              : t('photo_failed', 'Could not read the photo.');
          self._photoDone(msg, 'err');
          return;
        }
        self._applyPhotoResult(res.body);
      }).catch(function () {
        self._photoDone(t('photo_failed', 'Could not read the photo.'), 'err');
      });
    });
  };

  PerfSim.prototype._photoDone = function (msg, kind) {
    if (kind === 'err') this._aeError('photo_failed', { msg: String(msg).slice(0, 60) });
    this._photoBusy = false;
    var btn = this.$('pbPhotoBtn');
    if (btn) btn.disabled = !this._photoFile;
    this._photoStatus(msg, kind);
  };

  /** Feed the model's answer through the SAME importer a typed list goes through. */
  PerfSim.prototype._applyPhotoResult = function (body) {
    var imp = PB.importer.fromText(body.netlist || '');
    if (!imp.ok) { this._photoDone(t('photo_unreadable', 'No circuit could be read from that photo.'), 'err'); return; }

    // apply any values the model could read off the drawing
    var values = body.values || {};
    for (var i = 0; i < imp.specs.length; i++) {
      var v = values[imp.specs[i].ref];
      if (!v) continue;
      for (var k in v) { if (Object.prototype.hasOwnProperty.call(v, k) && isFinite(v[k])) imp.specs[i].props[k] = v[k]; }
    }

    // show the user what was read, so they can check it against the photo line by line
    var ta = this.$('pbImportText');
    if (ta) ta.value = body.netlist;

    this._aiDerived = true;          // marks the PNG export — AI Act Art. 50
    var okLoaded = this.loadSchematic(body.netlist);
    if (okLoaded) {
      // re-apply values AFTER the layout, since loadSchematic re-imported the text
      for (var c = 0; c < this.board.components.length; c++) {
        var comp = this.board.components[c], vv = values[comp.ref];
        if (!vv) continue;
        for (var kk in vv) { if (Object.prototype.hasOwnProperty.call(vv, kk) && isFinite(vv[kk])) comp.props[kk] = vv[kk]; }
      }
      this._persist(); this.draw();
    }

    var low = body.confidence !== 'high';
    var msg = (low ? t('photo_low_conf', 'Read with low confidence — check every connection against the photo.')
                   : t('photo_done', 'Read {n} parts. Check the board against the photo before you solder.'))
      .replace('{n}', body.parts);
    if (body.notes) msg += ' ' + body.notes;
    this._photoDone(msg, low ? 'warn' : 'ok');
    this._ae('tool_complete', { f: 'photo', parts: body.parts, conf: body.confidence });
  };

  /* ---------------- routing ---------------- */
  /** Re-run the router after a move, without shouting about it. */
  PerfSim.prototype._rerouteQuiet = function () {
    if (!this.target) return;
    this.board.autoroute(this.target);
  };

  PerfSim.prototype.autorouteNow = function () {
    if (!this.target) { this._toast(t('route_no_target', 'Import a schematic first — the router needs to know what to connect.'), 'warning'); return; }
    this._beginChange();
    var res = this.board.autoroute(this.target);
    this._after('autoroute');
    if (res.ok) this._toast(t('route_ok', 'Routed. Check the board before you solder.'), 'success');
    else this._toast(t('route_partial', 'Routed {n} of {m} nets — the rest need more room or a second side.')
      .replace('{n}', res.routed).replace('{m}', res.routed + res.failed.length), 'warning');
    this._ae('tool_complete', { routed: res.routed, failed: res.failed.length });
  };

  /* ---------------- verification + simulation ---------------- */
  PerfSim.prototype._updateStatus = function () {
    var el = this.$('pbStatus');
    if (!el) return;
    if (!this.target) {
      // Without an imported schematic there is nothing to CHECK the board against, but
      // the solver still has plenty to say — and saying nothing at all after someone
      // presses Simulate reads as a broken button.
      if (this.simulating) { this._status(el, this._simText(), this._simKind()); return; }
      el.style.display = 'none';
      return;
    }
    var v = this.board.verify(this.target);
    if (v.ok) { this._stage('verified'); this._status(el, t('verify_ok', 'Every connection in the schematic is on the board.'), 'ok'); }
    else if (v.shorts.length) this._status(el, t('verify_short', 'Short: {n} connection(s) join nets that must stay apart.').replace('{n}', v.shorts.length), 'error');
    else this._status(el, t('verify_missing', '{n} connection(s) still missing.').replace('{n}', v.missing.length), 'warn');
  };
  /** Turn the solver's outcome into something a person can act on. */
  PerfSim.prototype._simText = function () {
    if (!this.board.components.length) return t('sim_empty', 'The board is empty — place a part first.');
    var r = this.sim;
    if (!r) return t('sim_empty', 'The board is empty — place a part first.');
    if (!r.ok) {
      if (r.error === 'no_power_source') return t('sim_no_power', 'No power source — add a battery.');
      if (r.error === 'incomplete_circuit') return t('sim_incomplete', 'The circuit is not closed — some parts are not wired to anything.');
      return t('sim_error', 'The circuit does not solve — check the wiring.');
    }
    var w = r.warnings || [];
    if (w.indexOf('empty') >= 0) return t('sim_empty', 'The board is empty — place a part first.');
    if (w.indexOf('no_power_source') >= 0) return t('sim_no_power', 'No power source — add a battery.');
    if (w.indexOf('open_circuit') >= 0 || w.indexOf('incomplete_circuit') >= 0) {
      return this.board.traces.length
        ? t('sim_open', 'The circuit is open — the current has nowhere to flow. Check for a missing trace.')
        : t('sim_unwired', 'Nothing is connected yet — draw traces between the holes.');
    }
    if (w.indexOf('short_circuit') >= 0) return t('sim_short', 'Short circuit — the battery is wired straight across.');
    if (w.indexOf('led_overload') >= 0) return t('sim_overload', 'Too much current through the LED — add or increase a resistor.');
    // Honest partial-solve note: mount-only hardware (sockets, headers, terminals,
    // modules) has no electrical model — say so instead of implying it was solved.
    var mounts = 0;
    for (var mi = 0; mi < this.board.components.length; mi++) {
      if (Board.MOUNT_TYPES[this.board.components[mi].type]) mounts++;
    }
    if (mounts > 0) {
      return t('sim_ok', 'The circuit works. Values are shown on the parts.') + ' '
        + t('sim_partial', 'Mounting parts ({n}) are not simulated.').replace('{n}', mounts);
    }
    return t('sim_ok', 'The circuit works. Values are shown on the parts.');
  };
  PerfSim.prototype._simKind = function () {
    var r = this.sim;
    if (!this.board.components.length || !r) return 'warn';
    if (!r.ok) return 'error';
    var w = r.warnings || [];
    if (w.indexOf('short_circuit') >= 0 || w.indexOf('led_overload') >= 0) return 'error';
    if (w.length) return 'warn';
    return 'ok';
  };

  PerfSim.prototype._status = function (el, msg, kind) {
    el.style.display = '';
    el.className = 'pb-status pb-status-' + kind;
    el.textContent = msg;
  };

  PerfSim.prototype.toggleSim = function () {
    this.simulating = !this.simulating;
    var b = this.$('pbSimBtn');
    if (b) {
      b.innerHTML = this.simulating ? ('⏹ ' + esc(t('stop', 'Stop'))) : ('▶ ' + esc(t('simulate', 'Simulate')));
      b.classList.toggle('pb-sim-on', this.simulating);
    }
    if (this.simulating) { this.runSim(true); this._startRaf(); }
    else { this.sim = null; this._stopRaf(); this._updateStatus(); this.draw(); }
  };
  PerfSim.prototype._scheduleSim = function () {
    var self = this; clearTimeout(this._simT);
    this._simT = setTimeout(function () { self.runSim(false); }, 80);
  };
  PerfSim.prototype.runSim = function (userInitiated) {
    if (!CE) return;
    var net = this.board.netlist();
    this.sim = CE.simulate(net, { ideal: !!this._ideal });
    this._updateStatus();
    this.renderAssembly();
    if (userInitiated) this._ae('simulate', { parts: net.components.length });
    this.draw();
  };
  PerfSim.prototype._startRaf = function () {
    var self = this;
    if (this.raf) return;
    var tick = function () { self.phase += 0.03; self.draw(); self.raf = requestAnimationFrame(tick); };
    this.raf = requestAnimationFrame(tick);
  };
  PerfSim.prototype._stopRaf = function () { if (this.raf) cancelAnimationFrame(this.raf); this.raf = 0; };

  /* ---------------- properties panel ---------------- */
  PerfSim.prototype._showProps = function (comp) {
    var empty = this.$('pbPropsEmpty'), box = this.$('pbPropsContent');
    if (!empty || !box) return;
    // A new panel starts a new edit burst, so the next keystroke snapshots for undo
    // instead of folding into the previous part's entry.
    this._propEditing = false;
    if (!comp) { empty.style.display = ''; box.style.display = 'none'; box.innerHTML = ''; return; }
    empty.style.display = 'none'; box.style.display = '';
    var p = comp.props, h = '<div class="pb-prop-title">' + esc(comp.ref || comp.type) + '</div>';
    // free-text label for the generic chips — «микросхемы под ключ, без названий»:
    // the user writes NE555/LM358/ATtiny on the body themselves
    if (p.label != null) h += this._rowText('pb-prop-row', t('prop_label', 'Marking'), 'label', p.label);
    if (p.voltage != null) h += this._rowNum('pb-prop-row', t('prop_voltage', 'Voltage, V'), 'voltage', p.voltage, 0.1);
    if (p.resistance != null) h += this._rowNum('pb-prop-row', t('prop_resistance', 'Resistance, Ω'), 'resistance', p.resistance, 1);
    if (p.wiper != null) h += this._rowRange('pb-prop-row', t('prop_wiper', 'Wiper'), 'wiper', p.wiper, 0, 1, 0.01);
    if (p.vf != null) h += this._rowNum('pb-prop-row', t('prop_vf', 'Forward voltage, V'), 'vf', p.vf, 0.1);
    if (p.capacitance != null) h += this._rowNum('pb-prop-row', t('prop_capacitance', 'Capacitance, µF'), 'capacitance', p.capacitance, 1);
    if (p.color != null) h += this._rowColor('pb-prop-row', t('prop_color', 'Colour'), 'color', p.color);
    // `level` is shared by the photoresistor AND the thermistor, but it means a
    // different physical thing for each — the label must follow the TYPE. With a
    // blanket «Light» the thermistor slider claimed to set illumination
    // (Ivan 03.08: «у термистора в ползунке написано освещённость»).
    if (p.level != null) h += this._rowRange('pb-prop-row',
      comp.type === 'thermistor' ? t('prop_temp', 'Temperature') : t('prop_light', 'Light'),
      'level', p.level, 0, 1, 0.01);
    // Booleans need a control of their own. Without one a button or a switch sits on the
    // board as decoration: the circuit is open, the LED stays dark, and nothing the user
    // can click will change that.
    if (p.pressed != null) h += this._rowToggle('pressed', t('prop_pressed', 'Pressed'), p.pressed);
    if (p.closed != null) h += this._rowToggle('closed', t('prop_closed', 'Closed'), p.closed);
    h += '<div class="pb-prop-holes">' + esc(t('prop_holes', 'Holes')) + ': ' + this._holeList(comp) + '</div>';
    box.innerHTML = h;
    this._bindPropInputs(box);
    this._bindToggles(box);
  };
  /** A two-state button — SimCore.props only builds inputs, and a checkbox reads worse here. */
  PerfSim.prototype._rowToggle = function (prop, label, on) {
    return '<div class="pb-prop-row"><label>' + esc(label) + '</label>'
      + '<button type="button" class="pb-toggle' + (on ? ' on' : '') + '" data-toggle="' + esc(prop) + '">'
      + esc(on ? t('state_on', 'Yes') : t('state_off', 'No')) + '</button></div>';
  };
  PerfSim.prototype._bindToggles = function (box) {
    var self = this;
    var btns = box.querySelectorAll('[data-toggle]');
    for (var i = 0; i < btns.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var comp = self._compById(self.selected);
          if (!comp) return;
          var prop = btn.getAttribute('data-toggle');
          self._beginChange();
          comp.props[prop] = !comp.props[prop];
          self._showProps(comp);          // re-render so the label follows the state
          self._after('toggle_' + prop);
        });
      })(btns[i]);
    }
  };

  PerfSim.prototype._holeList = function (comp) {
    var b = this.board, out = [];
    for (var i = 0; i < comp.holes.length; i++) out.push(esc(b.holeName(comp.holes[i])));
    return out.join(' · ');
  };
  PerfSim.prototype._showTraceProps = function (tr) {
    var empty = this.$('pbPropsEmpty'), box = this.$('pbPropsContent');
    if (!empty || !box) return;
    empty.style.display = 'none'; box.style.display = '';
    var b = this.board;
    box.innerHTML = '<div class="pb-prop-title">' + esc(t('trace_title', 'Trace')) + '</div>'
      + '<div class="pb-prop-holes">' + esc(b.holeName(tr.a)) + ' → ' + esc(b.holeName(tr.b))
      + ' · ' + esc(tr.layer === 0 ? t('layer_bottom', 'Solder side') : t('layer_top', 'Component side')) + '</div>';
  };

  /**
   * SimCore.props seam. The shared binder already snapshotted for undo (once per edit
   * burst) before calling this, so do NOT _beginChange here — doing so would push one
   * undo entry per keystroke.
   */
  PerfSim.prototype._applyPropInput = function (prop, val, inputType) {
    var comp = this._compById(this.selected);
    if (!comp) return;
    if (inputType === 'number' || inputType === 'range') {
      if (!isFinite(val)) return;                     // mid-typing "1e", "-", "" — ignore, don't clobber
      if (prop === 'resistance' || prop === 'capacitance') val = Math.max(0, val);
      if (prop === 'wiper' || prop === 'level') val = Math.max(0, Math.min(1, val));
    }
    comp.props[prop] = val;
  };
  /** Called by the shared binder after each applied edit. */
  PerfSim.prototype._afterEdit = function () { this._after(); };

  /* ---------------- floating action bar ---------------- */
  PerfSim.prototype._positionActions = function () {
    if (!this.actions) return;
    if (!this.selected && !this.selTrace) { this._hideActions(); return; }
    // Pinned to a FIXED corner, never over the artwork: on a dense board a floating
    // bar sitting on the part swallows the next tap (the breadboard shipped that bug).
    this.actions.style.display = 'flex';
    var rot = this.actions.querySelector('[data-act=rotate]');
    if (rot) rot.style.display = this.selected ? '' : 'none';
  };
  PerfSim.prototype._hideActions = function () { if (this.actions) this.actions.style.display = 'none'; };

  /* ---------------- rendering ---------------- */
  PerfSim.prototype.draw = function () {
    var ctx = this.ctx, b = this.board;
    if (!ctx) return;
    var light = isLight();
    ctx.save();
    ctx.clearRect(0, 0, this.cssW, this.cssH);
    ctx.direction = 'ltr';   // designators and readings are LTR notation in every locale
    ctx.translate(this.cam.x, this.cam.y);
    ctx.scale(this.cam.s, this.cam.s);
    if (this.flipped) { ctx.translate(b.width(), 0); ctx.scale(-1, 1); }

    this._drawSubstrate(ctx, light);
    this._drawStrips(ctx, light);
    this._drawTraces(ctx, light);
    this._drawHoles(ctx, light);
    this._drawCuts(ctx);
    this._drawVias(ctx);
    this._drawComponents(ctx, light);
    this._drawNotes(ctx, light);
    this._drawHot(ctx);

    ctx.restore();
    this._updateScrollbars();
  };

  PerfSim.prototype._drawSubstrate = function (ctx, light) {
    var b = this.board, w = b.width(), h = b.height();
    // A two-sided board is fibreglass GREEN, a one-sided board the cheaper phenolic
    // TAN — the two really do look different in the hand, and Ivan noticed ours did
    // not. So the substrate colour now carries the side count at a glance.
    var two = b.sides === 2;
    ctx.fillStyle = two ? (light ? '#3f7d5a' : '#2b5a40') : (light ? '#d9c48a' : '#8f7a44');
    rr(ctx, 0, 0, w, h, 10); ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,.28)'; ctx.lineWidth = 1.5; ctx.stroke();
    // a corner tab telling you which side you are looking at
    ctx.save(); ctx.direction = 'ltr';
    ctx.fillStyle = two ? 'rgba(230,240,235,.75)' : 'rgba(60,45,20,.7)';
    ctx.font = '600 9px ui-sans-serif,system-ui,sans-serif'; ctx.textAlign = 'end';
    var side = this.flipped ? t('view_solder', 'Solder side') : t('view_component', 'Component side');
    var sides = two ? (b.plated ? t('sides_two_plated', '2-sided, plated') : t('sides_two', '2-sided')) : t('sides_one', '1-sided');
    // x=22 clears the corner mounting screw (centre 11, r≈3.5) — at 8 the first
    // glyph landed on the screw and «1-sided» read as «-sided» on the solder side
    if (this.flipped) { ctx.save(); ctx.translate(w, 0); ctx.scale(-1, 1); ctx.textAlign = 'start'; ctx.fillText(sides + ' · ' + side, 22, 16); ctx.restore(); }
    else ctx.fillText(sides + ' · ' + side, w - 22, 16);
    ctx.restore();
    // mounting holes at the corners, like a real board
    ctx.fillStyle = 'rgba(0,0,0,.45)';
    var m = 11;
    var pts = [[m, m], [w - m, m], [m, h - m], [w - m, h - m]];
    for (var i = 0; i < pts.length; i++) {
      ctx.beginPath(); ctx.arc(pts[i][0], pts[i][1], 3.4, 0, 6.2832); ctx.fill();
    }
  };

  /** Stripboard's pre-joined copper rows, drawn under the pads. */
  PerfSim.prototype._drawStrips = function (ctx) {
    var b = this.board;
    if (b.kind !== 'strip') return;
    ctx.strokeStyle = LAYER_COL[0]; ctx.globalAlpha = 0.5;
    ctx.lineWidth = PITCH * 0.5; ctx.lineCap = 'round';
    for (var r = 0; r < b.rows; r++) {
      var a = b.holeXY(b.idx(0, r)), z = b.holeXY(b.idx(b.cols - 1, r));
      line(ctx, a.x, a.y, z.x, z.y);
    }
    ctx.globalAlpha = 1;
  };

  PerfSim.prototype._drawTraces = function (ctx) {
    var b = this.board, i;
    // The side facing away from the viewer is drawn dimmer, "through" the board —
    // so which side a run is on is readable at a glance rather than guessed.
    var near = this.flipped ? 0 : 1;
    for (var pass = 0; pass < 2; pass++) {
      var layer = pass === 0 ? (near === 0 ? 1 : 0) : near;
      for (i = 0; i < b.traces.length; i++) {
        var tr = b.traces[i];
        if (tr.layer !== layer) continue;
        if (b.sides === 1 && layer !== 0) continue;
        var pa = b.holeXY(tr.a), pb = b.holeXY(tr.b);
        var sel = this.selTrace && this.selTrace.layer === tr.layer
          && this.selTrace.a === tr.a && this.selTrace.b === tr.b;
        ctx.globalAlpha = (layer === near) ? 1 : 0.38;
        ctx.strokeStyle = sel ? '#34d399' : LAYER_COL[layer];
        ctx.lineWidth = sel ? PITCH * 0.42 : PITCH * 0.34;
        ctx.lineCap = 'round';
        line(ctx, pa.x, pa.y, pb.x, pb.y);
      }
    }
    ctx.globalAlpha = 1;
  };

  PerfSim.prototype._drawHoles = function (ctx, light) {
    var b = this.board, n = b.holeCount();
    // plated-through holes get the shiny tinned ring of a factory board —
    // the visual tell that every hole connects both sides
    var padCol = b.plated ? (light ? '#c8cdd4' : '#9aa2ad') : (light ? '#b89a5c' : '#6d5c33');
    for (var i = 0; i < n; i++) {
      var p = b.holeXY(i);
      ctx.beginPath(); ctx.arc(p.x, p.y, PITCH * 0.28, 0, 6.2832);
      ctx.fillStyle = padCol;
      ctx.fill();
      ctx.beginPath(); ctx.arc(p.x, p.y, PITCH * 0.14, 0, 6.2832);
      ctx.fillStyle = light ? '#3a2f18' : '#1a1408';
      ctx.fill();
    }
  };

  PerfSim.prototype._drawCuts = function (ctx) {
    var b = this.board;
    ctx.strokeStyle = '#ef4444'; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
    for (var i = 0; i < b.cuts.length; i++) {
      var c = b.cuts[i], pa = b.holeXY(c.a), pb = b.holeXY(c.b);
      var mx = (pa.x + pb.x) / 2, my = (pa.y + pb.y) / 2, s = PITCH * 0.26;
      line(ctx, mx - s, my - s, mx + s, my + s);
      line(ctx, mx + s, my - s, mx - s, my + s);
    }
  };

  /** Prompt for a hole label; the coordinate is offered as a default so "14-5"-style
   *  labelling (Ivan's "like battleship") is one tap plus Enter. */
  PerfSim.prototype._editNote = function (hole) {
    var b = this.board, cur = b.getNote(hole);
    var coord = b.holeName(hole);
    var txt;
    try {
      txt = window.prompt(t('note_prompt', 'Label for hole {c}:').replace('{c}', coord), cur || '');
    } catch (e) { txt = null; }
    if (txt === null) return;                 // cancelled
    this._beginChange();
    b.setNote(hole, txt);
    this._after('note');
  };

  PerfSim.prototype._drawNotes = function (ctx, light) {
    var b = this.board;
    ctx.save();
    ctx.direction = 'ltr';
    ctx.textAlign = 'start';
    ctx.font = '600 9px ui-sans-serif,system-ui,sans-serif';
    for (var hk in b.notes) {
      if (!Object.prototype.hasOwnProperty.call(b.notes, hk)) continue;
      var hole = parseInt(hk, 10);
      if (!(hole >= 0 && hole < b.holeCount())) continue;
      var p = b.holeXY(hole), text = b.notes[hk];
      // a small amber flag pinned to the hole
      ctx.beginPath(); ctx.arc(p.x, p.y, PITCH * 0.2, 0, 6.2832);
      ctx.fillStyle = '#f59e0b'; ctx.fill();
      var tw = ctx.measureText(text).width;
      var bx = p.x + PITCH * 0.28, by = p.y - PITCH * 0.5;
      var draw = function () {
        rr(ctx, bx - 3, by - 2, tw + 8, 14, 4);
        ctx.fillStyle = light ? 'rgba(255,247,230,.95)' : 'rgba(60,44,16,.92)'; ctx.fill();
        ctx.strokeStyle = 'rgba(245,158,11,.55)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.fillStyle = light ? '#7a5312' : '#fcd7a1';
        ctx.fillText(text, bx + 1, by + 9);
      };
      if (this.flipped) { ctx.save(); ctx.translate(b.width(), 0); ctx.scale(-1, 1); bx = b.width() - bx - tw; draw(); ctx.restore(); }
      else draw();
    }
    ctx.restore();
  };

  PerfSim.prototype._drawVias = function (ctx) {
    var b = this.board;
    for (var i = 0; i < b.vias.length; i++) {
      var p = b.holeXY(b.vias[i]);
      ctx.beginPath(); ctx.arc(p.x, p.y, PITCH * 0.24, 0, 6.2832);
      ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 3; ctx.stroke();
      ctx.strokeStyle = '#6b7280'; ctx.lineWidth = 1.2; ctx.stroke();
    }
  };

  PerfSim.prototype._drawHot = function (ctx) {
    if (this.hotHole < 0) return;
    if (this.mode === 'select') return;
    var p = this.board.holeXY(this.hotHole);
    ctx.beginPath(); ctx.arc(p.x, p.y, PITCH * 0.38, 0, 6.2832);
    ctx.strokeStyle = '#38bdf8'; ctx.lineWidth = 2; ctx.stroke();
  };

  /* ---------------- component art ---------------- */
  PerfSim.prototype._drawComponents = function (ctx, light) {
    var b = this.board, i;
    for (i = 0; i < b.components.length; i++) {
      var c = b.components[i];
      var pts = c.holes.map(function (h) { return b.holeXY(h); });

      // A DIP part (the smart block) does not lie along a single axis, so it is drawn
      // straight in world space over its two rows rather than in a rotated leg frame.
      if (this._footprint(c).layout === 'dip') { this._drawDip(ctx, c, pts, light); continue; }

      var a = pts[0], z = pts[pts.length - 1];
      var cx = (a.x + z.x) / 2, cy = (a.y + z.y) / 2;
      var ang = Math.atan2(z.y - a.y, z.x - a.x);
      var len = Math.hypot(z.x - a.x, z.y - a.y);
      var flipText = false;
      if (ang > Math.PI / 2 || ang < -Math.PI / 2) { ang += Math.PI; flipText = true; }

      // LEADS FIRST, so the body sits on top of where they enter it. Ivan asked "where
      // are the legs of the transistors, LEDs, resistors?" — the honest answer was that
      // they were never drawn. Every part now grows real tinned legs from each hole to
      // its body, plus a solder blob at the hole. Blobs are drawn in WORLD space (one
      // save/restore), then the legs and body in the rotated leg frame (another) — no
      // interleaved transform juggling.
      var bodyHalf = this._bodyHalf(c.type, len), pp;
      ctx.save();
      for (pp = 0; pp < pts.length; pp++) {
        ctx.beginPath(); ctx.arc(pts[pp].x, pts[pp].y, PITCH * 0.16, 0, 6.2832);
        ctx.fillStyle = light ? '#b9bfc7' : '#8b929c'; ctx.fill();
      }
      ctx.restore();

      ctx.save();
      // On the solder side you look at the BACK of the board — the bodies sit behind it, so
      // fade them and their legs to let the pads and traces you are soldering read through
      // (Ivan 2026-07-24: "со стороны пайки компоненты закрывают всё — сделайте прозрачнее").
      // The solder blobs above stay solid: those are the joints you actually work on.
      if (this.flipped) ctx.globalAlpha = 0.4;
      ctx.translate(cx, cy); ctx.rotate(ang);
      ctx.strokeStyle = light ? '#9198a1' : '#c9ccd1'; ctx.lineWidth = 2.4; ctx.lineCap = 'round';
      if (c.type === 'transistor') {
        // A TO-92 stands ABOVE the board on three splayed legs — all three must be visible.
        // Drawn flat before, the middle leg ran straight up UNDER the round body and vanished,
        // so it read as a two-leg part (Ivan 2026-07-24: "у транзистора всё ещё 2 ножки, а не 3").
        // Ride the body up by bodyUp and fan every leg from its hole to the body's flat underside.
        var bodyUp = PITCH * 0.62, foot = -bodyUp + PITCH * 0.42;
        for (pp = 0; pp < pts.length; pp++) {
          var tlx = pts.length === 1 ? 0 : (-len / 2 + pp * len / (pts.length - 1));
          line(ctx, tlx, 0, tlx * 0.42, foot);
        }
        if (this.flipped) { ctx.scale(-1, 1); }
        ctx.save(); ctx.translate(0, -bodyUp); this._drawBody(ctx, c, len, light); ctx.restore();
      } else {
        for (pp = 0; pp < pts.length; pp++) {
          var lx = pts.length === 1 ? 0 : (-len / 2 + pp * len / (pts.length - 1));
          var target = Math.abs(lx) <= bodyHalf ? lx : (lx < 0 ? -bodyHalf : bodyHalf);
          line(ctx, lx, 0, target, Math.abs(lx) <= bodyHalf ? -PITCH * 0.32 : 0);
        }
        if (this.flipped) { ctx.scale(-1, 1); }
        this._drawBody(ctx, c, len, light);
      }
      ctx.restore();

      this._drawRef(ctx, c, cx, cy, len, flipText, light);
      if (this.selected === c.id) {
        ctx.strokeStyle = '#34d399'; ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx, cy, Math.max(len / 2 + PITCH * 0.5, PITCH * 0.8), 0, 6.2832);
        ctx.stroke();
      }
    }
  };

  PerfSim.prototype._footprint = function (c) { return CAT[c.type] || {}; };

  /** Half-width of a part's body along its leg axis, used to land the legs on it. */
  PerfSim.prototype._bodyHalf = function (type, len) {
    switch (type) {
      case 'resistor': return Math.max(len - PITCH * 0.9, PITCH * 0.8) / 2;
      case 'diode':    return Math.max(len - PITCH * 0.9, PITCH * 0.7) / 2;
      case 'led':      return PITCH * 0.36;
      case 'capacitor': return PITCH * 0.42;
      case 'transistor': return PITCH * 0.44;
      case 'battery':  return Math.max(len + PITCH * 0.6, PITCH * 1.6) / 2;
      case 'button': case 'switch': return Math.max(len, PITCH * 0.9) / 2;
      case 'fuse':     return Math.max(len - PITCH * 0.4, PITCH * 1.2) / 2;
      default: return Math.max(len, PITCH * 0.8) / 2;
    }
  };

  /** A DIP chip drawn over its two rows, with a notch, a pin-1 dot and numbered legs. */
  PerfSim.prototype._drawDip = function (ctx, c, pts, light) {
    var b = this.board, i;
    var minx = 1e9, miny = 1e9, maxx = -1e9, maxy = -1e9;
    for (i = 0; i < pts.length; i++) {
      if (pts[i].x < minx) minx = pts[i].x; if (pts[i].x > maxx) maxx = pts[i].x;
      if (pts[i].y < miny) miny = pts[i].y; if (pts[i].y > maxy) maxy = pts[i].y;
    }
    // silver legs from each hole to the body edge
    // solder blobs stay SOLID — those are the joints you work on
    var midY = (miny + maxy) / 2;
    for (i = 0; i < pts.length; i++) {
      ctx.beginPath(); ctx.arc(pts[i].x, pts[i].y, PITCH * 0.16, 0, 6.2832);
      ctx.fillStyle = light ? '#b9bfc7' : '#8b929c'; ctx.fill();
    }
    // Legs and body fade on the solder side like every other part. This branch
    // exits _drawComponents before the shared alpha is set, so the DIP kept full
    // opacity on the flipped view while everything around it went translucent
    // (Ivan #6414: "со стороны пайки детали прозрачные, а умный блок нет").
    ctx.save();
    if (this.flipped) ctx.globalAlpha = 0.4;
    ctx.strokeStyle = light ? '#9198a1' : '#c9ccd1'; ctx.lineWidth = 2.4; ctx.lineCap = 'round';
    for (i = 0; i < pts.length; i++) {
      var toY = pts[i].y < midY ? miny + PITCH * 0.42 : maxy - PITCH * 0.42;
      line(ctx, pts[i].x, pts[i].y, pts[i].x, toY);
    }
    // body inset from the rows
    var bx = minx - PITCH * 0.28, by = miny + PITCH * 0.42;
    var bw = (maxx - minx) + PITCH * 0.56, bh = (maxy - miny) - PITCH * 0.84;
    if (this._footprint(c).socket) {
      // DIP SOCKET: a hollow plastic frame — the middle is simply NOT painted
      // (evenodd ring), so the board reads through: that is the point of a socket
      var wx = bx + PITCH * 0.5, wy = by + PITCH * 0.52, ww = Math.max(bw - PITCH, 0), wh = Math.max(bh - PITCH * 1.04, 0);
      ctx.beginPath();
      ctx.rect(bx, by, bw, bh);
      if (ww > 2 && wh > 2) ctx.rect(wx, wy, ww, wh);
      ctx.fillStyle = '#1f242e'; ctx.fill('evenodd');
      ctx.strokeStyle = 'rgba(255,255,255,.16)'; ctx.lineWidth = 1;
      ctx.strokeRect(bx, by, bw, bh);
      if (ww > 2 && wh > 2) { ctx.strokeStyle = 'rgba(255,255,255,.10)'; ctx.strokeRect(wx, wy, ww, wh); }
      // key notch on the LEFT short edge, like the real part (Ivan 31.07: «ключ,
      // и от него как всегда слева 1 и т.д.» — it used to sit top-centre)
      ctx.beginPath(); ctx.arc(bx, by + bh / 2, PITCH * 0.16, -Math.PI / 2, Math.PI / 2); ctx.fillStyle = '#0a0c10'; ctx.fill();
    } else {
      ctx.fillStyle = '#15181f'; rr(ctx, bx, by, bw, bh, 3); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,.14)'; ctx.lineWidth = 1; ctx.stroke();
      // key notch on the LEFT short edge + pin-1 dot at bottom-left next to it
      ctx.beginPath(); ctx.arc(bx, by + bh / 2, PITCH * 0.18, -Math.PI / 2, Math.PI / 2); ctx.fillStyle = '#0a0c10'; ctx.fill();
      ctx.beginPath(); ctx.arc(bx + PITCH * 0.28, by + bh - PITCH * 0.28, PITCH * 0.09, 0, 6.2832);
      ctx.fillStyle = '#9ca3af'; ctx.fill();
    }
    // DIP pin numbers, counted the DIP way: 1 at bottom-left by the key, along
    // the bottom row, then back right-to-left along the top row. Purely
    // geometric (independent of internal hole order), tiny digits by each leg.
    ctx.save(); ctx.direction = 'ltr';
    ctx.font = '600 6px ui-sans-serif,system-ui,sans-serif'; ctx.textAlign = 'center';
    ctx.fillStyle = light ? 'rgba(40,45,52,.75)' : 'rgba(200,206,214,.75)';
    var bot = [], top = [];
    for (i = 0; i < pts.length; i++) (pts[i].y > midY ? bot : top).push(pts[i]);
    bot.sort(function (a, z) { return a.x - z.x; });
    top.sort(function (a, z) { return z.x - a.x; });
    var ordered = bot.concat(top);
    for (i = 0; i < ordered.length; i++) {
      var np = ordered[i], nyy = np.y > midY ? np.y + PITCH * 0.44 : np.y - PITCH * 0.26;
      if (this.flipped) {
        ctx.save(); ctx.translate(np.x, 0); ctx.scale(-1, 1);
        ctx.fillText(String(i + 1), 0, nyy);
        ctx.restore();
      } else ctx.fillText(String(i + 1), np.x, nyy);
    }
    ctx.restore();
    // "IC" label
    ctx.save(); ctx.direction = 'ltr';
    ctx.fillStyle = 'rgba(220,224,230,.85)';
    ctx.font = '700 8px ui-sans-serif,system-ui,sans-serif'; ctx.textAlign = 'center';
    var dipLabel = (c.props && c.props.label) ? String(c.props.label).slice(0, 12) : (c.ref || 'IC');
    if (this.flipped) { ctx.translate(bx + bw / 2, 0); ctx.scale(-1, 1); ctx.fillText(esc(dipLabel), 0, midY + 3); }
    else ctx.fillText(esc(dipLabel), bx + bw / 2, midY + 3);
    ctx.restore();
    ctx.restore();
    // selection ring
    if (this.selected === c.id) {
      ctx.strokeStyle = '#34d399'; ctx.lineWidth = 2;
      rr(ctx, bx - 3, by - 3, bw + 6, bh + 6, 5); ctx.stroke();
    }
  };

  PerfSim.prototype._drawRef = function (ctx, c, cx, cy, len, flipText, light) {
    ctx.save();
    ctx.direction = 'ltr';
    if (this.flipped) { ctx.translate(this.board.width(), 0); ctx.scale(-1, 1); cx = this.board.width() - cx; }
    ctx.fillStyle = light ? 'rgba(30,30,30,.8)' : 'rgba(240,240,240,.85)';
    ctx.font = '600 9px ui-sans-serif,system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(c.ref || '', cx, cy - PITCH * 0.68);
    ctx.restore();
    void len; void flipText;
  };

  PerfSim.prototype._drawBody = function (ctx, c, len, light) {
    var res = this.sim && this.sim.ok ? this.sim.components[c.id] : null;
    switch (c.type) {
      case 'jumper': {
        // insulated wire: gentle arch between the legs, round caps, breadboard-blue
        var jw = Math.max(len - PITCH * 0.4, PITCH * 0.5), jh = Math.min(PITCH * 0.9, jw * 0.35);
        ctx.strokeStyle = '#2563eb'; ctx.lineWidth = PITCH * 0.22; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(-jw / 2, 0); ctx.quadraticCurveTo(0, -jh, jw / 2, 0); ctx.stroke();
        ctx.strokeStyle = 'rgba(255,255,255,.25)'; ctx.lineWidth = PITCH * 0.07;
        ctx.beginPath(); ctx.moveTo(-jw / 2, -PITCH * 0.04); ctx.quadraticCurveTo(0, -jh - PITCH * 0.04, jw / 2, -PITCH * 0.04); ctx.stroke();
        ctx.lineCap = 'butt';
        break;
      }
      case 'resistor': {
        var bw = Math.max(len - PITCH * 0.9, PITCH * 0.8), bh = PITCH * 0.5;
        // beige body with rounded ends, a subtle top highlight, real colour bands
        var g = ctx.createLinearGradient(0, -bh / 2, 0, bh / 2);
        g.addColorStop(0, '#e8dcb8'); g.addColorStop(0.5, '#d8c9a0'); g.addColorStop(1, '#c3b488');
        ctx.fillStyle = g; rr(ctx, -bw / 2, -bh / 2, bw, bh, bh / 2); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.28)'; ctx.lineWidth = 0.8; ctx.stroke();
        var bands = resistorBands(c.props.resistance);
        for (var i = 0; i < bands.length; i++) {
          ctx.fillStyle = bands[i];
          ctx.fillRect(-bw / 2 + bw * (0.2 + i * 0.16), -bh / 2, bw * 0.08, bh);
        }
        ctx.fillStyle = RES_GOLD;
        ctx.fillRect(bw / 2 - bw * 0.18, -bh / 2, bw * 0.065, bh);
        break;
      }
      case 'led': {
        // brightness follows the current: dark below ~2% of the rating, halo ramps
        var visp = res ? Math.abs(res.current || 0) / ((c.props.imax || 0.02)) : 0;
        var lit = visp > 0.02;
        var col = c.props.color || '#ef4444';
        if (lit) {
          var gg = ctx.createRadialGradient(0, 0, 1, 0, 0, PITCH * 1.15);
          gg.addColorStop(0, col); gg.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.globalAlpha = 0.15 + 0.45 * Math.min(1, visp); ctx.fillStyle = gg;
          ctx.beginPath(); ctx.arc(0, 0, PITCH * 1.15, 0, 6.2832); ctx.fill();
          ctx.globalAlpha = 1;
        }
        // domed lens: radial shading + a white catch-light, flat on the cathode side
        var lg = ctx.createRadialGradient(-PITCH * 0.12, -PITCH * 0.12, 1, 0, 0, PITCH * 0.4);
        lg.addColorStop(0, lit ? '#fff' : shade(col, 30));
        lg.addColorStop(0.35, lit ? col : shade(col, -20));
        lg.addColorStop(1, lit ? shade(col, -30) : shade(col, -70));
        ctx.beginPath(); ctx.arc(0, 0, PITCH * 0.38, 0, 6.2832); ctx.fillStyle = lg; ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.4)'; ctx.lineWidth = 1; ctx.stroke();
        // cathode flat
        ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = 2;
        line(ctx, PITCH * 0.3, -PITCH * 0.24, PITCH * 0.3, PITCH * 0.24);
        break;
      }
      case 'diode': {
        var dw = Math.max(len - PITCH * 0.9, PITCH * 0.7), dh = PITCH * 0.42;
        var dgr = ctx.createLinearGradient(0, -dh / 2, 0, dh / 2);
        dgr.addColorStop(0, '#3a3a3a'); dgr.addColorStop(0.5, '#1f1f1f'); dgr.addColorStop(1, '#111');
        ctx.fillStyle = dgr; rr(ctx, -dw / 2, -dh / 2, dw, dh, 3); ctx.fill();
        // white cathode stripe near one end
        ctx.fillStyle = '#e5e7eb'; ctx.fillRect(dw / 2 - dw * 0.2, -dh / 2, dw * 0.1, dh);
        break;
      }
      case 'capacitor': {
        // electrolytic can: dark cylinder, silver top, a minus stripe down the "−" side
        var r0 = PITCH * 0.46;
        var cg = ctx.createLinearGradient(-r0, 0, r0, 0);
        cg.addColorStop(0, '#0f2a6b'); cg.addColorStop(0.5, '#1f3a8a'); cg.addColorStop(1, '#0f2a6b');
        ctx.beginPath(); ctx.arc(0, 0, r0, 0, 6.2832); ctx.fillStyle = cg; ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.3)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.fillStyle = 'rgba(210,220,240,.85)';
        ctx.beginPath(); ctx.arc(0, 0, r0 * 0.42, 0, 6.2832); ctx.fill();          // vent top
        // minus band
        ctx.fillStyle = '#cbd5e1';
        ctx.save(); ctx.direction = 'ltr'; ctx.font = '700 9px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('−', -r0 * 0.66, 3); ctx.restore();
        break;
      }
      case 'battery': {
        var w = Math.max(len + PITCH * 0.6, PITCH * 1.6), hgt = PITCH * 0.66;
        var bgr = ctx.createLinearGradient(0, -hgt / 2, 0, hgt / 2);
        bgr.addColorStop(0, '#374151'); bgr.addColorStop(0.5, '#1f2937'); bgr.addColorStop(1, '#111827');
        ctx.fillStyle = bgr; rr(ctx, -w / 2, -hgt / 2, w, hgt, 4); ctx.fill();
        ctx.fillStyle = '#ef4444'; ctx.fillRect(-w / 2, -hgt / 2, w * 0.14, hgt);   // + end wrap
        ctx.save(); ctx.direction = 'ltr';
        ctx.fillStyle = '#e5e7eb'; ctx.font = '700 8px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText((c.props.voltage || 9) + 'V', 0, 3); ctx.restore();
        break;
      }
      case 'transistor': {
        // TO-92: flat-fronted black half-disc with a metallic sheen
        var tg = ctx.createLinearGradient(0, -PITCH * 0.44, 0, PITCH * 0.44);
        tg.addColorStop(0, '#2a2f3a'); tg.addColorStop(0.5, '#12151c'); tg.addColorStop(1, '#0a0c10');
        ctx.beginPath();
        ctx.arc(0, 0, PITCH * 0.44, Math.PI * 0.72, Math.PI * 2.28);
        ctx.closePath(); ctx.fillStyle = tg; ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1; ctx.stroke();
        // flat face highlight
        ctx.strokeStyle = 'rgba(255,255,255,.12)'; ctx.lineWidth = 1.4;
        line(ctx, -PITCH * 0.36, PITCH * 0.28, PITCH * 0.36, PITCH * 0.28);
        break;
      }
      case 'potentiometer': {
        // A pot reads as a knurled dial with a wiper mark, not a nameless blob — its
        // whole point is that it turns, so it should look turnable.
        var pr = Math.max(len * 0.4, PITCH * 0.5);
        var pg = ctx.createRadialGradient(-pr * 0.3, -pr * 0.3, 1, 0, 0, pr);
        pg.addColorStop(0, '#5b616b'); pg.addColorStop(1, '#2b2f36');
        ctx.beginPath(); ctx.arc(0, 0, pr, 0, 6.2832); ctx.fillStyle = pg; ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.4)'; ctx.lineWidth = 1; ctx.stroke();
        // knurling ticks
        ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1;
        for (var k = 0; k < 12; k++) {
          var aa = k / 12 * 6.2832;
          line(ctx, Math.cos(aa) * pr * 0.78, Math.sin(aa) * pr * 0.78, Math.cos(aa) * pr, Math.sin(aa) * pr);
        }
        // wiper indicator, pointing to the current setting
        var wv = (c.props.wiper != null ? c.props.wiper : 0.5);
        var wa = (-0.75 + wv * 1.5) * Math.PI;
        ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 2;
        line(ctx, 0, 0, Math.cos(wa) * pr * 0.75, Math.sin(wa) * pr * 0.75);
        break;
      }
      case 'buzzer': {
        // A buzzer is a round can with a sound hole in the top and a "+" — reads as a
        // buzzer, not the grey lozenge it used to be.
        var br = Math.max(len * 0.42, PITCH * 0.52);
        var zg = ctx.createRadialGradient(-br * 0.3, -br * 0.3, 1, 0, 0, br);
        zg.addColorStop(0, '#33373f'); zg.addColorStop(1, '#0f1116');
        ctx.beginPath(); ctx.arc(0, 0, br, 0, 6.2832); ctx.fillStyle = zg; ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.16)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.beginPath(); ctx.arc(0, 0, br * 0.22, 0, 6.2832); ctx.fillStyle = '#05060a'; ctx.fill(); // sound port
        ctx.save(); ctx.direction = 'ltr';
        ctx.fillStyle = 'rgba(220,224,230,.8)'; ctx.font = '700 8px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('+', br * 0.5, -br * 0.4); ctx.restore();
        break;
      }
      case 'button': {
        // Momentary: a round cap on a square base — clearly a thing you PRESS.
        var sb = Math.max(len, PITCH * 0.9), sh = PITCH * 0.66;
        ctx.fillStyle = '#3a4150'; rr(ctx, -sb / 2, -sh / 2, sb, sh, 3); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.35)'; ctx.lineWidth = 1; ctx.stroke();
        var pressed = !!c.props.pressed;
        var cg2 = ctx.createRadialGradient(-2, -2, 1, 0, 0, PITCH * 0.26);
        cg2.addColorStop(0, pressed ? '#34d399' : '#cbd0d8');
        cg2.addColorStop(1, pressed ? '#0f8f6b' : '#7c828c');
        ctx.beginPath(); ctx.arc(0, 0, PITCH * (pressed ? 0.2 : 0.26), 0, 6.2832);
        ctx.fillStyle = cg2; ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.3)'; ctx.lineWidth = 0.8; ctx.stroke();
        break;
      }
      case 'switch': {
        // Toggle: a slide track with the actuator at one end — visibly different from a
        // button, which Ivan called out as looking identical.
        var wsw = Math.max(len, PITCH * 1.0), hsw = PITCH * 0.56;
        ctx.fillStyle = '#2b303a'; rr(ctx, -wsw / 2, -hsw / 2, wsw, hsw, hsw / 2); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.35)'; ctx.lineWidth = 1; ctx.stroke();
        var closed = !!c.props.closed;
        var lever = closed ? wsw * 0.22 : -wsw * 0.22;
        ctx.fillStyle = closed ? '#34d399' : '#9ca3af';
        rr(ctx, lever - PITCH * 0.16, -hsw / 2 + 2, PITCH * 0.32, hsw - 4, 3); ctx.fill();
        break;
      }
      case 'photoresistor': {
        // LDR: light tan face with the classic serpentine track.
        var lw = Math.max(len - PITCH * 0.6, PITCH * 0.7), lh = PITCH * 0.56;
        ctx.fillStyle = '#c8b27a'; rr(ctx, -lw / 2, -lh / 2, lw, lh, 3); ctx.fill();
        ctx.strokeStyle = '#5b4a24'; ctx.lineWidth = 1.4;
        ctx.beginPath();
        for (var sx = -lw / 2 + 3; sx < lw / 2 - 2; sx += 4) {
          ctx.moveTo(sx, -lh / 2 + 2); ctx.lineTo(sx + 2, lh / 2 - 2);
        }
        ctx.stroke();
        break;
      }
      case 'inductor': {
        var iw = Math.max(len - PITCH * 0.8, PITCH * 0.9);
        ctx.strokeStyle = light ? '#8a6b3a' : '#c99b5a'; ctx.lineWidth = 2.4;
        for (var cc = 0; cc < 4; cc++) {
          ctx.beginPath();
          ctx.arc(-iw / 2 + iw * (cc + 0.5) / 4, 0, iw / 9, Math.PI, 0, false);
          ctx.stroke();
        }
        break;
      }
      case 'lamp': {
        // incandescent lamp: glass globe with a looped filament
        var lar = Math.max(len * 0.42, PITCH * 0.52);
        var lag = ctx.createRadialGradient(-lar * 0.3, -lar * 0.35, 1, 0, 0, lar);
        lag.addColorStop(0, '#fff7d0'); lag.addColorStop(1, light ? '#e3c256' : '#c19a3c');
        ctx.beginPath(); ctx.arc(0, 0, lar, 0, 6.2832); ctx.fillStyle = lag; ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.35)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.strokeStyle = '#7a5b1a'; ctx.lineWidth = 1.3;                    // filament ⋀
        line(ctx, -lar * 0.5, lar * 0.45, -lar * 0.16, -lar * 0.32);
        line(ctx, -lar * 0.16, -lar * 0.32, lar * 0.16, -lar * 0.32);
        line(ctx, lar * 0.16, -lar * 0.32, lar * 0.5, lar * 0.45);
        break;
      }
      case 'fuse': {
        // glass cartridge: translucent tube, metal end caps, a thin element wire
        var fw = Math.max(len - PITCH * 0.4, PITCH * 1.2), fh = PITCH * 0.5, cap = fw * 0.16;
        ctx.fillStyle = light ? 'rgba(170,195,220,.55)' : 'rgba(150,175,200,.32)';
        rr(ctx, -fw / 2, -fh / 2, fw, fh, fh / 2); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.28)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.fillStyle = light ? '#9aa2ad' : '#b2bac5';
        rr(ctx, -fw / 2, -fh / 2, cap, fh, 2); ctx.fill();
        rr(ctx, fw / 2 - cap, -fh / 2, cap, fh, 2); ctx.fill();
        ctx.strokeStyle = '#c98a3a'; ctx.lineWidth = 1.2;
        line(ctx, -fw / 2 + cap, 0, fw / 2 - cap, 0);
        break;
      }
      case 'motor': {
        // DC motor can with an "M" (M is symmetric, so it survives the solder-side mirror)
        var mr = Math.max(len * 0.42, PITCH * 0.56);
        var mg = ctx.createRadialGradient(-mr * 0.3, -mr * 0.3, 1, 0, 0, mr);
        mg.addColorStop(0, '#8a929c'); mg.addColorStop(1, '#3c424b');
        ctx.beginPath(); ctx.arc(0, 0, mr, 0, 6.2832); ctx.fillStyle = mg; ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.4)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.save(); ctx.direction = 'ltr'; ctx.fillStyle = '#eef1f4';
        ctx.font = '700 ' + Math.round(mr * 1.15) + 'px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('M', 0, 1); ctx.restore();
        break;
      }
      case 'thermistor': {
        // thermistor bead: dark disc crossed by the temperature-dependence stroke
        var thr = Math.max(len * 0.4, PITCH * 0.5);
        var thg = ctx.createRadialGradient(-thr * 0.3, -thr * 0.3, 1, 0, 0, thr);
        thg.addColorStop(0, '#5a6470'); thg.addColorStop(1, '#2b323b');
        ctx.beginPath(); ctx.arc(0, 0, thr, 0, 6.2832); ctx.fillStyle = thg; ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.2)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.strokeStyle = '#ffb020'; ctx.lineWidth = 1.5;
        line(ctx, -thr * 0.72, thr * 0.72, thr * 0.72, -thr * 0.72);
        break;
      }
      case 'bridge': {
        // KBP-style molded rectifier: an IC-like brick over the in-line legs, the
        // «+» corner chamfered like the real package, EVERY pin marked ~ ~ + −
        // (Ivan 31.07: «сделайте нормальный диодный мост в виде микросхемы с 4
        // ногами, а эта странная какая-то» — the old render was a thin bar).
        var brw = len + PITCH * 0.7, brh = PITCH * 1.7, brTop = -brh + PITCH * 0.12;
        var brChamfer = PITCH * 0.42;
        var brg = ctx.createLinearGradient(0, brTop, 0, brTop + brh);
        brg.addColorStop(0, '#3a3f49'); brg.addColorStop(1, '#1e222a');
        ctx.beginPath();
        ctx.moveTo(-brw / 2 + 2.5, brTop);
        ctx.lineTo(brw / 2 - brChamfer, brTop);                    // chamfer at the + side
        ctx.lineTo(brw / 2, brTop + brChamfer);
        ctx.lineTo(brw / 2, brTop + brh - 2.5);
        ctx.arcTo(brw / 2, brTop + brh, brw / 2 - 2.5, brTop + brh, 2.5);
        ctx.lineTo(-brw / 2 + 2.5, brTop + brh);
        ctx.arcTo(-brw / 2, brTop + brh, -brw / 2, brTop + brh - 2.5, 2.5);
        ctx.lineTo(-brw / 2, brTop + 2.5);
        ctx.arcTo(-brw / 2, brTop, -brw / 2 + 2.5, brTop, 2.5);
        ctx.closePath();
        ctx.fillStyle = brg; ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 0.9; ctx.stroke();
        // diode-diamond silk in the middle
        var bcy = brTop + brh * 0.42, brd = PITCH * 0.3;
        ctx.strokeStyle = '#cbd2da'; ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.moveTo(-brd, bcy); ctx.lineTo(0, bcy - brd); ctx.lineTo(brd, bcy); ctx.lineTo(0, bcy + brd); ctx.closePath();
        ctx.stroke();
        // per-pin marks over each leg: ~ ~ + −  (hole order = BRIDGE_PINS)
        ctx.save(); ctx.direction = 'ltr';
        ctx.fillStyle = 'rgba(228,232,238,.9)'; ctx.font = '600 8px ui-sans-serif,system-ui,sans-serif'; ctx.textAlign = 'center';
        var brMarks = ['~', '~', '+', '−'];
        for (var bm = 0; bm < 4; bm++) {
          var bmx = -len / 2 + bm * len / 3;
          ctx.fillText(brMarks[bm], bmx, brTop + brh - 3.5);
        }
        ctx.restore();
        break;
      }
      case 'header4': case 'header8': {
        // pin header: black plastic strip with gold square pins at each hole
        var hn = CAT[c.type].pins, hw = len + PITCH * 0.7, hh = PITCH * 0.5;
        ctx.fillStyle = '#15181f'; rr(ctx, -hw / 2, -hh / 2, hw, hh, 1.5); ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.14)'; ctx.lineWidth = 0.8; ctx.stroke();
        ctx.fillStyle = '#d9b45b';
        for (var hp = 0; hp < hn; hp++) {
          var hx = hn === 1 ? 0 : (-len / 2 + hp * len / (hn - 1));
          ctx.fillRect(hx - PITCH * 0.09, -PITCH * 0.09, PITCH * 0.18, PITCH * 0.18);
        }
        break;
      }
      case 'terminal2': case 'terminal3': {
        // screw terminal block: blue body, one screw head per pole
        var tn = CAT[c.type].pins, tw = len + PITCH * 0.9, th = PITCH * 0.86;
        var tg2 = ctx.createLinearGradient(0, -th / 2, 0, th / 2);
        tg2.addColorStop(0, '#2f6fd8'); tg2.addColorStop(1, '#1d4ea6');
        ctx.fillStyle = tg2; rr(ctx, -tw / 2, -th / 2, tw, th, 2.5); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.35)'; ctx.lineWidth = 0.8; ctx.stroke();
        for (var tp = 0; tp < tn; tp++) {
          var tx = tn === 1 ? 0 : (-len / 2 + tp * len / (tn - 1));
          ctx.beginPath(); ctx.arc(tx, -th * 0.12, PITCH * 0.17, 0, 6.2832);
          ctx.fillStyle = '#c7ccd4'; ctx.fill();
          ctx.strokeStyle = '#5a6068'; ctx.lineWidth = 1;
          line(ctx, tx - PITCH * 0.11, -th * 0.12, tx + PITCH * 0.11, -th * 0.12);   // screw slot
        }
        break;
      }
      case 'module4': {
        // generic 4-pin module: green PCB riser with silk outline + corner hole
        var mw = len + PITCH * 0.9, mh = PITCH * 1.15;
        var mg2 = ctx.createLinearGradient(0, -mh, 0, 0);
        mg2.addColorStop(0, '#1d7a4f'); mg2.addColorStop(1, '#115c3a');
        ctx.fillStyle = mg2; rr(ctx, -mw / 2, -mh + PITCH * 0.1, mw, mh, 2); ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.22)'; ctx.lineWidth = 0.8;
        rr(ctx, -mw / 2 + 2, -mh + PITCH * 0.1 + 2, mw - 4, mh - 4, 1.5); ctx.stroke();
        ctx.beginPath(); ctx.arc(mw / 2 - PITCH * 0.22, -mh + PITCH * 0.32, PITCH * 0.1, 0, 6.2832);
        ctx.fillStyle = '#0b3a24'; ctx.fill();
        break;
      }
      default: {
        var gw = Math.max(len, PITCH * 0.8), gh = PITCH * 0.52;
        var dg = ctx.createLinearGradient(0, -gh / 2, 0, gh / 2);
        dg.addColorStop(0, light ? '#aab0b8' : '#565d68'); dg.addColorStop(1, light ? '#8b929c' : '#3a4048');
        ctx.fillStyle = dg; rr(ctx, -gw / 2, -gh / 2, gw, gh, 3); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.25)'; ctx.lineWidth = 0.8; ctx.stroke();
      }
    }
  };

  /* ================================================================== *
   * Assembly aids — what you need once the board is designed and it is
   * time to actually buy parts and heat the iron.
   * ================================================================== */

  /** 470 → "470 Ω", 4700 → "4.7 kΩ", 1e6 → "1 MΩ" */
  function fmtNum(v, unit) {
    if (!isFinite(v)) return '';
    var a = Math.abs(v), s, p = '';
    if (a >= 1e6) { s = v / 1e6; p = 'M'; }
    else if (a >= 1e3) { s = v / 1e3; p = 'k'; }
    else { s = v; }
    var str = Math.abs(s - Math.round(s)) < 1e-9 ? String(Math.round(s)) : String(Math.round(s * 100) / 100);
    return str + ' ' + p + unit;
  }
  PerfSim.prototype._valueOf = function (c) {
    var p = c.props || {};
    switch (c.type) {
      case 'resistor': case 'potentiometer': case 'buzzer': case 'lamp': case 'motor':
        return p.resistance != null ? fmtNum(p.resistance, t('unit_ohm', 'Ω')) : '';
      case 'capacitor':  return p.capacitance != null ? (p.capacitance + ' ' + t('unit_uf', 'µF')) : '';
      case 'battery':    return p.voltage != null ? (p.voltage + ' ' + t('unit_v', 'V')) : '';
      case 'diode':      return p.vf != null ? (p.vf + ' ' + t('unit_v', 'V')) : '';
      case 'inductor':   return p.inductance != null ? fmtNum(p.inductance, t('unit_h', 'H')) : '';
      case 'fuse':       return p.imax != null ? (p.imax + ' ' + t('unit_a', 'A')) : '';
      case 'transistor': return p.kind ? String(p.kind).toUpperCase() : '';
      default: return '';
    }
  };

  /**
   * Shopping list. Grouped by type AND value, because two 470 Ω resistors are one line
   * on a shopping list and a 470 and a 10k are two.
   */
  PerfSim.prototype.buildBom = function () {
    var by = {}, order = [], i;
    for (i = 0; i < this.board.components.length; i++) {
      var c = this.board.components[i];
      var val = this._valueOf(c);
      var key = c.type + '|' + val;
      if (!by[key]) { by[key] = { type: c.type, value: val, refs: [], count: 0 }; order.push(key); }
      by[key].refs.push(c.ref || '');
      by[key].count++;
    }
    return order.map(function (k) { return by[k]; });
  };

  PerfSim.prototype.bomAsText = function () {
    return this.buildBom().map(function (row) {
      return row.count + ' x ' + t('comp_' + row.type, row.type)
        + (row.value ? ' ' + row.value : '')
        + '  (' + row.refs.join(', ') + ')';
    }).join('\n');
  };

  /**
   * Soldering order. Real boards are built lowest-profile first: once a tall
   * electrolytic is in, you cannot lay the board face-down to solder a flat resistor.
   * Wire links go first of all because they run UNDER everything else.
   */
  var SOLDER_ORDER = [
    { key: 'links', types: [] },
    { key: 'flat',  types: ['jumper', 'resistor', 'diode', 'header4', 'header8'] },
    { key: 'ic',    types: ['mcu', 'socket8', 'socket14', 'socket16', 'chip8', 'chip14', 'chip16'] },
    { key: 'mid',   types: ['transistor', 'bridge', 'led', 'photoresistor', 'thermistor', 'button', 'switch'] },
    { key: 'tall',  types: ['capacitor', 'potentiometer', 'inductor', 'buzzer', 'lamp', 'fuse', 'motor', 'terminal2', 'terminal3', 'module4'] },
    { key: 'last',  types: ['battery'] }
  ];
  PerfSim.prototype.buildSolderSteps = function () {
    var steps = [], i, g, c, used = {};
    for (g = 0; g < SOLDER_ORDER.length; g++) {
      var grp = SOLDER_ORDER[g], items = [];
      if (grp.key === 'links') {
        for (i = 0; i < this.board.vias.length; i++) {
          items.push({ id: 'via' + this.board.vias[i], label: t('step_via', 'Wire link') + ' ' + this.board.holeName(this.board.vias[i]) });
        }
      } else {
        for (i = 0; i < this.board.components.length; i++) {
          c = this.board.components[i];
          if (grp.types.indexOf(c.type) < 0 || used[c.id]) continue;
          used[c.id] = 1;
          items.push({
            id: c.id,
            label: (c.ref || c.type) + ' — ' + t('comp_' + c.type, c.type)
              + (this._valueOf(c) ? ' ' + this._valueOf(c) : '')
              + ' · ' + this._holeNames(c)
          });
        }
      }
      if (items.length) steps.push({ key: grp.key, items: items });
    }
    var rest = [];
    for (i = 0; i < this.board.components.length; i++) {
      c = this.board.components[i];
      if (!used[c.id]) rest.push({ id: c.id, label: (c.ref || c.type) + ' · ' + this._holeNames(c) });
    }
    if (rest.length) steps.push({ key: 'other', items: rest });
    return steps;
  };
  PerfSim.prototype._holeNames = function (c) {
    var b = this.board, out = [];
    for (var i = 0; i < c.holes.length; i++) out.push(b.holeName(c.holes[i]));
    return out.join('-');
  };

  /**
   * Polarity check, read off the SOLVED circuit rather than guessed from the layout.
   *
   * A part that only works one way round and is sitting the other way round is the
   * single most common build mistake, and it is invisible once soldered — both ends
   * look identical. We know which pin is the anode and we know the node voltages, so
   * this is a statement of fact, not a heuristic.
   */
  PerfSim.prototype.polarityWarnings = function () {
    var out = [], r = this.sim;
    if (!r || !r.ok || !r.nodes || !r.pinNodes) return out;
    var b = this.board;
    for (var i = 0; i < b.components.length; i++) {
      var c = b.components[i];
      if (c.type !== 'led' && c.type !== 'diode') continue;
      var va = r.nodes[r.pinNodes[c.id + '#0']];
      var vk = r.nodes[r.pinNodes[c.id + '#1']];
      if (va == null || vk == null) continue;
      var cur = r.components[c.id] ? Math.abs(r.components[c.id].current || 0) : 0;
      if ((va - vk) < -0.05 && cur < 1e-5) {
        out.push({
          ref: c.ref || c.type,
          msg: t('polarity_reversed', '{ref} is in backwards — the anode must face the positive side.')
            .replace('{ref}', c.ref || c.type)
        });
      }
    }
    return out;
  };

  PerfSim.prototype._assemblyKey = function () {
    // Tie the "soldered" ticks to THIS board: a different layout must not inherit them.
    var sig = this.board.components.map(function (c) { return c.type + c.holes.join('.'); }).join('|');
    var h = 0;
    for (var i = 0; i < sig.length; i++) { h = ((h << 5) - h + sig.charCodeAt(i)) | 0; }
    return 'done_' + (h >>> 0).toString(36);
  };
  PerfSim.prototype._loadDone = function () {
    try {
      if (!window.__toolSettings) return {};
      var raw = window.__toolSettings.load('perfboard-simulator', this._assemblyKey(), null);
      return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
    } catch (e) { return {}; }
  };
  PerfSim.prototype._saveDone = function (map) {
    try { if (window.__toolSettings) window.__toolSettings.save('perfboard-simulator', this._assemblyKey(), JSON.stringify(map)); } catch (e) {}
  };

  PerfSim.prototype.renderAssembly = function () {
    var box = this.$ ? this.$('pbAssembly') : null;
    if (!box) return;
    var bom = this.buildBom();
    if (!bom.length) {
      box.innerHTML = '<div class="pb-props-empty">' + esc(t('assembly_empty', 'Place some parts and the shopping list and soldering order will appear here.')) + '</div>';
      return;
    }
    var self = this, h = '', i;

    h += '<div class="pb-asm-title">' + esc(t('bom_title', 'Parts to buy')) + '</div><table class="pb-bom">';
    for (i = 0; i < bom.length; i++) {
      h += '<tr><td class="pb-bom-n">' + bom[i].count + ' x</td><td>' + esc(t('comp_' + bom[i].type, bom[i].type))
        + (bom[i].value ? ' <b>' + esc(bom[i].value) + '</b>' : '')
        + '</td><td class="pb-bom-refs">' + esc(bom[i].refs.join(', ')) + '</td></tr>';
    }
    h += '</table><button type="button" class="pb-btn" id="pbBomCopy">' + esc(t('bom_copy', 'Copy the list')) + '</button>';

    var warn = this.polarityWarnings();
    if (warn.length) {
      h += '<div class="pb-asm-title" style="margin-top:18px">' + esc(t('polarity_title', 'Polarity')) + '</div><ul class="pb-polarity">';
      for (i = 0; i < warn.length; i++) h += '<li>' + esc(warn[i].msg) + '</li>';
      h += '</ul>';
    }

    var steps = this.buildSolderSteps(), done = this._loadDone(), total = 0, ticked = 0;
    if (steps.length) {
      h += '<div class="pb-asm-title" style="margin-top:18px">' + esc(t('solder_title', 'Soldering order')) + '</div>';
      h += '<p class="pb-import-hint">' + esc(t('solder_hint', 'Lowest parts first: once a tall capacitor is in you can no longer lay the board face-down.')) + '</p>';
      for (var g = 0; g < steps.length; g++) {
        h += '<div class="pb-step-group">' + esc(t('step_' + steps[g].key, steps[g].key)) + '</div><ul class="pb-steps">';
        for (var k = 0; k < steps[g].items.length; k++) {
          var it = steps[g].items[k];
          total++;
          if (done[it.id]) ticked++;
          h += '<li><label><input type="checkbox" data-step="' + esc(it.id) + '"' + (done[it.id] ? ' checked' : '') + '> '
            + '<span' + (done[it.id] ? ' class="pb-step-done"' : '') + '>' + esc(it.label) + '</span></label></li>';
        }
        h += '</ul>';
      }
      h += '<div class="pb-asm-progress">' + esc(t('solder_progress', 'Soldered {a} of {b}').replace('{a}', ticked).replace('{b}', total)) + '</div>';
    }
    box.innerHTML = h;

    var copy = this.$('pbBomCopy');
    if (copy) copy.addEventListener('click', function () {
      var txt = self.bomAsText();
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(txt);
        else if (self._fallbackCopy) self._fallbackCopy(txt, function () {});
      } catch (e) {}
      self._toast(t('bom_copied', 'The list is on your clipboard.'), 'success');
      self._ae('feature_use', { f: 'bom_copy' });
    });
    var boxes = box.querySelectorAll('[data-step]');
    for (var b2 = 0; b2 < boxes.length; b2++) {
      (function (cb) {
        cb.addEventListener('change', function () {
          var map = self._loadDone();
          if (cb.checked) map[cb.getAttribute('data-step')] = 1;
          else delete map[cb.getAttribute('data-step')];
          self._saveDone(map);
          self.renderAssembly();
          self._ae('feature_use', { f: 'solder_tick' });
        });
      })(boxes[b2]);
    }
  };

  /* ---------------- print at true 1:1 scale ---------------- */

  /**
   * The board as an SVG sized in MILLIMETRES, so a browser print comes out at true
   * scale and the sheet can be laid under the real board. That is the whole reason a
   * perfboard plan is worth printing — a drawing at 0.9x is worse than no drawing, so
   * a 100 mm ruler prints alongside to catch a printer that rescaled.
   */
  PerfSim.prototype.buildPrintSvg = function (mirror) {
    var b = this.board, PITCH_MM = 2.54;
    var mmPerWorld = PITCH_MM / Board.PITCH;
    var wW = b.width(), wH = b.height();
    var mmW = (wW * mmPerWorld).toFixed(2), mmH = (wH * mmPerWorld).toFixed(2);
    var i, p, out = [];

    out.push('<svg xmlns="http://www.w3.org/2000/svg" width="' + mmW + 'mm" height="' + mmH + 'mm" viewBox="0 0 ' + wW + ' ' + wH + '">');
    out.push('<g' + (mirror ? ' transform="translate(' + wW + ',0) scale(-1,1)"' : '') + '>');
    out.push('<rect x="0" y="0" width="' + wW + '" height="' + wH + '" fill="none" stroke="#000" stroke-width="1"/>');

    if (b.kind === 'strip') {
      for (var r = 0; r < b.rows; r++) {
        var a1 = b.holeXY(b.idx(0, r)), z1 = b.holeXY(b.idx(b.cols - 1, r));
        out.push('<line x1="' + a1.x + '" y1="' + a1.y + '" x2="' + z1.x + '" y2="' + z1.y + '" stroke="#bbb" stroke-width="' + (Board.PITCH * 0.5) + '" stroke-linecap="round"/>');
      }
    }
    // bottom traces solid, top dashed — one black-and-white sheet still tells them apart
    for (i = 0; i < b.traces.length; i++) {
      var tr = b.traces[i], pa = b.holeXY(tr.a), pb2 = b.holeXY(tr.b);
      out.push('<line x1="' + pa.x + '" y1="' + pa.y + '" x2="' + pb2.x + '" y2="' + pb2.y
        + '" stroke="#000" stroke-width="' + (Board.PITCH * 0.3) + '" stroke-linecap="round"'
        + (tr.layer === 1 ? ' stroke-dasharray="6 5"' : '') + '/>');
    }
    for (i = 0; i < b.holeCount(); i++) {
      p = b.holeXY(i);
      out.push('<circle cx="' + p.x + '" cy="' + p.y + '" r="' + (Board.PITCH * 0.13) + '" fill="#000"/>');
    }
    for (i = 0; i < b.cuts.length; i++) {
      var ca = b.holeXY(b.cuts[i].a), cb = b.holeXY(b.cuts[i].b);
      var mx = (ca.x + cb.x) / 2, my = (ca.y + cb.y) / 2, sz = Board.PITCH * 0.26;
      out.push('<path d="M' + (mx - sz) + ' ' + (my - sz) + 'L' + (mx + sz) + ' ' + (my + sz)
        + 'M' + (mx + sz) + ' ' + (my - sz) + 'L' + (mx - sz) + ' ' + (my + sz) + '" stroke="#000" stroke-width="2"/>');
    }
    for (i = 0; i < b.vias.length; i++) {
      p = b.holeXY(b.vias[i]);
      out.push('<circle cx="' + p.x + '" cy="' + p.y + '" r="' + (Board.PITCH * 0.26) + '" fill="none" stroke="#000" stroke-width="2"/>');
    }
    for (i = 0; i < b.components.length; i++) {
      var c = b.components[i];
      var first = b.holeXY(c.holes[0]), last = b.holeXY(c.holes[c.holes.length - 1]);
      out.push('<line x1="' + first.x + '" y1="' + first.y + '" x2="' + last.x + '" y2="' + last.y
        + '" stroke="#000" stroke-width="1.5" stroke-dasharray="3 3"/>');
      for (var h2 = 0; h2 < c.holes.length; h2++) {
        p = b.holeXY(c.holes[h2]);
        out.push('<circle cx="' + p.x + '" cy="' + p.y + '" r="' + (Board.PITCH * 0.3) + '" fill="none" stroke="#000" stroke-width="1.5"/>');
      }
      var cx2 = (first.x + last.x) / 2, cy2 = (first.y + last.y) / 2;
      // Labels sit INSIDE the mirrored group, so un-mirror each one — otherwise the
      // solder-side sheet prints every designator back to front, which is exactly the
      // confusion the mirrored sheet exists to remove.
      var lbl = '<text x="' + cx2 + '" y="' + (cy2 - Board.PITCH * 0.55) + '" font-size="10" text-anchor="middle" font-family="sans-serif">' + esc(c.ref || '') + '</text>';
      out.push(mirror ? '<g transform="translate(' + (2 * cx2) + ',0) scale(-1,1)">' + lbl + '</g>' : lbl);
    }
    // hole notes — a part the palette can't supply lives ONLY as a note, so it must
    // survive onto the printed sheet or the plan loses it. Un-mirror the text.
    for (var nk in b.notes) {
      if (!Object.prototype.hasOwnProperty.call(b.notes, nk)) continue;
      var nh = parseInt(nk, 10);
      if (!(nh >= 0 && nh < b.holeCount())) continue;
      var np = b.holeXY(nh), ntext = esc(b.notes[nk]);
      out.push('<circle cx="' + np.x + '" cy="' + np.y + '" r="' + (Board.PITCH * 0.2) + '" fill="none" stroke="#000" stroke-width="1.5"/>');
      var nlbl = '<text x="' + (np.x + Board.PITCH * 0.3) + '" y="' + (np.y - Board.PITCH * 0.3) + '" font-size="9" font-family="sans-serif">' + ntext + '</text>';
      out.push(mirror ? '<g transform="translate(' + (2 * (np.x + Board.PITCH * 0.3)) + ',0) scale(-1,1)">' + nlbl + '</g>' : nlbl);
    }
    out.push('</g></svg>');
    return { svg: out.join(''), mmW: mmW, mmH: mmH };
  };

  PerfSim.prototype.openPrint = function () {
    var host = this.$('pbPrint');
    if (!host) return;
    // The print stylesheet hides body>* and re-shows .pb-print — but a child of a
    // display:none ancestor stays invisible, so the sheet printed BLANK while the
    // host lived inside the app markup (Ivan: "выдает просто белый лист"). Reparent
    // it to <body> so the body>* rule can actually spare it.
    if (host.parentNode !== document.body) document.body.appendChild(host);
    var comp = this.buildPrintSvg(false), sold = this.buildPrintSvg(true);
    var b = this.board;
    var head = '<div class="pb-print-meta">' + esc(t('print_board', 'Board')) + ': '
      + b.cols + 'x' + b.rows + ' - ' + esc(t(b.kind === 'strip' ? 'kind_strip' : 'kind_perf', b.kind))
      + ' - ' + esc(t(b.sides === 2 ? 'sides_two' : 'sides_one', '')) + ' - ' + comp.mmW + 'x' + comp.mmH + ' mm</div>';
    // The ruler is the point: if this line does not measure 100 mm the printer rescaled
    // and nothing else on the sheet can be trusted.
    var ruler = '<div class="pb-ruler-wrap"><svg xmlns="http://www.w3.org/2000/svg" width="100mm" height="8mm" viewBox="0 0 100 8">'
      + '<line x1="0" y1="6" x2="100" y2="6" stroke="#000" stroke-width="0.4"/>';
    for (var mm = 0; mm <= 100; mm += 10) {
      ruler += '<line x1="' + mm + '" y1="' + (mm % 50 === 0 ? 1 : 3) + '" x2="' + mm + '" y2="6" stroke="#000" stroke-width="0.4"/>';
    }
    ruler += '</svg><div class="pb-print-meta">' + esc(t('print_ruler', 'This line must measure exactly 100 mm. If it does not, print again at 100% scale.')) + '</div></div>';

    host.innerHTML = head + ruler
      + '<div class="pb-print-sheet"><h3>' + esc(t('view_component', 'Component side')) + '</h3>' + comp.svg + '</div>'
      + '<div class="pb-print-sheet"><h3>' + esc(t('view_solder', 'Solder side')) + '</h3>' + sold.svg + '</div>';
    this._stage('exported', { f: 'print' });
    this._ae('feature_use', { f: 'print' });
    try { window.print(); } catch (e) {}
  };

  /* ---------------- UI wiring ---------------- */
  PerfSim.prototype._bindUI = function () {
    var self = this, $ = this.$;
    function on(id, ev, fn) { var el = $(id); if (el) el.addEventListener(ev, fn); }

    on('pbSelectBtn', 'click', function () { self.setMode('select'); });
    on('pbTraceBtn', 'click', function () { self.setMode(self.mode === 'trace' ? 'select' : 'trace'); });
    on('pbCutBtn', 'click', function () { self.setMode(self.mode === 'cut' ? 'select' : 'cut'); });
    on('pbViaBtn', 'click', function () { self.setMode(self.mode === 'via' ? 'select' : 'via'); });
    on('pbNoteBtn', 'click', function () { self.setMode(self.mode === 'note' ? 'select' : 'note'); });
    on('pbFlipBtn', 'click', function () { self.setFlipped(!self.flipped); self._ae('feature_use', { f: 'flip' }); });
    on('pbUndoBtn', 'click', function () { self.undo(); });
    on('pbKbdBtn', 'click', function () { self.showKbdHelp(); });
    on('pbRedoBtn', 'click', function () { self.redo(); });
    on('pbFitBtn', 'click', function () { self._fitBoard(); self.draw(); });
    on('pbZoomIn', 'click', function () { self.zoomBy(1.2); });
    on('pbZoomOut', 'click', function () { self.zoomBy(1 / 1.2); });
    on('pbZoomFit', 'click', function () { self._fitBoard(); self.draw(); });
    on('pbSimBtn', 'click', function () { self.toggleSim(); });
    on('pbRouteBtn', 'click', function () { self.autorouteNow(); });
    var exSel = $('pbExamples');
    if (exSel) {
      exSel.addEventListener('change', function () {
        var key = exSel.value;
        if (!key) return;
        var src = PB.importer.EXAMPLES[key];
        if (src) { self.loadSchematic(src); self._ae('feature_use', { f: 'example_' + key }); }
        exSel.value = '';
      });
    }
    on('pbImportBtn', 'click', function () {
      var ta = $('pbImportText');
      if (ta) self.loadSchematic(ta.value);
    });
    var drop = $('pbPhotoDrop');
    if (drop) {
      // RACE-SAFE binding, not a raw addEventListener: on a weak phone over slow
      // 4G this heavy page (sim-core + engine + board + ui) parses so late that
      // the user picks a photo BEFORE this listener attaches — the uploader logs
      // file_picked, the event is lost, and the "Read" button stays disabled
      // forever. Ivan hit exactly that (devMem=2, 4G, three picks in a minute,
      // zero photo_read events — «не получается загрузить фото», 29.07 23:10).
      // __bindUploaderFile replays el.__pendingFile for late listeners.
      var onPhotoFile = function (f) {
        if (!f) return;
        self._photoFile = f;
        var b = $('pbPhotoBtn');
        if (b) b.disabled = false;
        self._photoStatus('', 'busy');
      };
      if (typeof window.__bindUploaderFile === 'function') {
        window.__bindUploaderFile(drop, onPhotoFile);
      } else {
        drop.addEventListener('tool-uploader:file', function (e) {
          onPhotoFile(e.detail && (e.detail.file || (e.detail.files && e.detail.files[0])));
        });
      }
    }
    on('pbPhotoBtn', 'click', function () { self.readPhoto(); });
    on('pbClearBtn', 'click', function () { self.clearAll(); });
    on('pbPngBtn', 'click', function () { self.exportPng(); });
    on('pbPrintBtn', 'click', function () { self.openPrint(); });

    var pal = document.querySelectorAll('#pbPalette .pb-comp-btn');
    for (var i = 0; i < pal.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var ty = btn.getAttribute('data-type');
          self.setMode(self.mode === 'place:' + ty ? 'select' : 'place:' + ty);
        });
      })(pal[i]);
    }

    if (this.actions) {
      this.actions.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-act]') : null;
        if (!b) return;
        var act = b.getAttribute('data-act');
        if (act === 'delete') self.deleteSelected();
        else if (act === 'rotate') self.rotateSelected();
      });
    }

    function num(id) { var el = $(id); return el ? parseInt(el.value, 10) : null; }
    on('pbKind', 'change', function () { self.reconfigure({ kind: $('pbKind').value }); });
    on('pbSides', 'change', function () {
      var sv = $('pbSides').value;   // '1' | '2' | '2p' (plated-through)
      self.reconfigure({ sides: sv === '1' ? 1 : 2, plated: sv === '2p' });
    });
    on('pbCols', 'change', function () { self.reconfigure({ cols: num('pbCols') }); });
    on('pbRows', 'change', function () { self.reconfigure({ rows: num('pbRows') }); });
    on('pbLayerSel', 'change', function () { self.layer = parseInt($('pbLayerSel').value, 10) || 0; self.draw(); });

    this._bindShare();
    if (SC.models && this._bindModels) this._bindModels();

    // bare-key shortcuts (Ctrl+R belongs to the browser). Skip while typing.
    document.addEventListener('keydown', function (e) {
      var tag = (e.target && e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      // Ctrl+Z / Ctrl+Y (Ivan, #6821: "клавиша контрл з не работает"). The letter comes from
      // window.__keyLetter, not e.key — on the Русская layout e.key is 'я', and that must still undo.
      var kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        if (kl === 'z' && !e.shiftKey) { e.preventDefault(); self.undo(); return; }
        if (kl === 'y' || (kl === 'z' && e.shiftKey)) { e.preventDefault(); self.redo(); return; }
        return;
      }
      if (e.altKey) return;
      var k = kl || String(e.key || '').toLowerCase();
      if (k === 'delete' || k === 'backspace') { self.deleteSelected(); e.preventDefault(); }
      else if (k === 'v') self.setMode('select');
      else if (k === 'd') self.setMode('trace');
      else if (k === 'f') self.setFlipped(!self.flipped);
      else if (k === 'r') self.rotateSelected();
    });
  };


  PerfSim.prototype.showKbdHelp = function () {
    var self = this;
    if (this._kbdCard) { this._kbdCard.parentNode.removeChild(this._kbdCard); this._kbdCard = null; return; }
    var rows = [
      ['V', t('tool_select', 'Select')],
      ['D', t('tool_trace', 'Trace')],
      ['F', t('view_component', 'Flip side')],
      ['R', t('rotate', 'Rotate')],
      ['Delete', t('delete', 'Delete')],
      ['Ctrl+Z', t('undo', 'Undo')],
      ['Ctrl+Y', t('redo', 'Redo')]
    ];
    var wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1300;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px';
    var html = '<div style="background:var(--bg-secondary,#111826);border:1px solid var(--border,rgba(255,255,255,.14));border-radius:14px;max-width:380px;width:100%;padding:14px;font-size:13px;color:var(--text-primary,#e2e8f0)">'
      + '<div style="display:flex;align-items:center;margin-bottom:8px"><b style="flex:1">' + esc(t('kbd_title', 'Keyboard shortcuts')) + '</b>'
      + '<button type="button" data-close style="background:none;border:0;color:var(--text-muted,#94a3b8);font-size:18px;cursor:pointer;padding:2px 8px">&times;</button></div><table style="border-collapse:collapse">';
    for (var i = 0; i < rows.length; i++) {
      html += '<tr><td style="padding:3px 8px"><kbd style="background:var(--glass,rgba(255,255,255,.08));border:1px solid var(--border,rgba(255,255,255,.15));border-radius:4px;padding:1px 6px">' + rows[i][0] + '</kbd></td><td style="padding:3px 8px">' + esc(rows[i][1]) + '</td></tr>';
    }
    html += '</table></div>';
    wrap.innerHTML = html;
    document.body.appendChild(wrap);
    this._kbdCard = wrap;
    wrap.addEventListener('click', function (e) { if (e.target === wrap || e.target.closest('[data-close]')) { wrap.parentNode.removeChild(wrap); self._kbdCard = null; } });
    this._ae('feature_use', { f: 'kbd_help' });
  };

  /* ---------------- PNG export ---------------- */
  PerfSim.prototype.exportPng = function () {
    this._stage('exported', { ai: !!this._aiDerived });
    var b = this.board, pad = 16;
    var w = Math.ceil(b.width() + pad * 2), h = Math.ceil(b.height() + pad * 2);
    var off = document.createElement('canvas');
    var scale = 2;
    off.width = w * scale; off.height = h * scale;
    var octx = off.getContext('2d');
    octx.scale(scale, scale);
    octx.fillStyle = isLight() ? '#f6f7fb' : '#0b1120';
    octx.fillRect(0, 0, w, h);

    // swap the live camera/context for the export frame, draw, then swap back
    var savedCtx = this.ctx, savedCam = this.cam, savedW = this.cssW, savedH = this.cssH;
    this.ctx = octx; this.cam = { x: pad, y: pad, s: 1 }; this.cssW = w; this.cssH = h;
    var savedHot = this.hotHole; this.hotHole = -1;
    this.draw();
    this.ctx = savedCtx; this.cam = savedCam; this.cssW = savedW; this.cssH = savedH; this.hotHole = savedHot;
    this.draw();

    var self = this;
    off.toBlob(function (blob) {
      if (!blob) return;
      // A board read off a photo by a model is AI-derived output, so the exported
      // image carries a machine-readable mark (AI Act Art. 50 transparency). A board
      // the user drew by hand is not marked — marking everything would make the mark
      // meaningless.
      var finish = function (out) {
        try { if (window.__downloadFile) window.__downloadFile(out || blob, 'perfboard.png'); } catch (e) {}
        self._ae('tool_complete', { f: 'png', ai: !!self._aiDerived });
      };
      if (self._aiDerived && window.__aiMark && window.__aiMark.png) {
        try {
          window.__aiMark.png(blob, 'perfboard-simulator').then(finish).catch(function () { finish(null); });
          return;
        } catch (e) {}
      }
      finish(null);
    }, 'image/png');
  };

  /* ---------------- mixins ---------------- */
  SC.mixin(PerfSim.prototype, SC.camera);
  SC.mixin(PerfSim.prototype, SC.pointer, ['_onPointerDown', '_onPointerMove', '_onPointerUp']);
  SC.mixin(PerfSim.prototype, SC.history, ['serializeState', 'applyState']);
  SC.mixin(PerfSim.prototype, SC.share, ['_encodeShare', '_decodeShare', '_isShareEmpty']);
  if (SC.models) SC.mixin(PerfSim.prototype, SC.models);
  SC.mixin(PerfSim.prototype, SC.props, ['_applyPropInput', '_afterEdit']);

  /* ---------------- boot ---------------- */
  function boot() {
    if (!document.getElementById('pbCanvas')) return;
    var app = new PerfSim();
    window.__perfSim = app;
    app.init();
    // NB: no Timbrica3D.init here — that core belongs to the 3d-printing suite and is
    // not loaded on this page, so calling it was a no-op dressed up as telemetry setup.
    // tool_view is recorded server-side by TrackToolView; the funnel below is ours.
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.PerfSim = PerfSim;
})();
